/*
 * ttm-fx.js — shared motion layer for the How it Works and Totem Compass App pages.
 *
 * Everything is driven by data attributes so sections stay declarative:
 *   [data-ttmx-reveal]              fades/slides in once it enters the viewport (.is-in)
 *   [data-ttmx-progress="sticky"]   sets --p (0..1) while a tall sticky wrapper scrolls past
 *   [data-ttmx-progress="view"]     sets --p (0..1) from entering to leaving the viewport
 *   [data-ttmx-phases="3"]          (with progress) sets data-phase 0..n-1 and drives compasses
 *   [data-ttmx-steps]               sticky storytelling; the step crossing mid-screen is active
 *   [data-ttmx-xp]                  exploded hardware view; steps set data-focus via data-ttmx-focus
 *   [data-ttmx-tabs]                tab/list switcher, optional autoplay
 *   [data-ttmx-count]               counts up to its number on first view
 *   [data-ttmx-compass]             animated Totem Compass illustration
 *   [data-ttmx-mesh]                interactive Unity Mesh Network demo (canvas)
 *   [data-ttmx-hscroll]             horizontal track pinned while you scroll down
 *   [data-ttmx-glow]                pointer-follow glow on cards (--mx / --my)
 *   [data-ttmx-stack]               photo stack: cards deal in, then cycle on a timer
 *   [data-ttmx-mq]                  (on a phases pin) phases only run while this media query matches
 *   [data-ttmx-tabsync]             (on a phases pin) each phase shows the matching tab
 *   [data-ttmx-chapters]            side table of contents + floating CTA
 *   script[data-ttmx-glossary]      JSON term list; first mention per section gets a "?" tooltip
 *
 * Safe to include more than once: the first copy wins. Re-initialises sections the
 * theme editor re-renders (shopify:section:load).
 */
(function () {
  if (window.__ttmx) return;
  window.__ttmx = true;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var clamp = function (v, a, b) { return Math.max(a, Math.min(b, v)); };
  var qa = function (root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

  /* ------------------------------------------------------------------ */
  /* Colours — the Totem Compass App palette (crystalColors.ts)          */
  /* ------------------------------------------------------------------ */
  var C = {
    red: '#FF2A1F', orange: '#FF7A00', yellow: '#FFD400', lime: '#9BE800', green: '#17E85C',
    mint: '#00E8A0', teal: '#00CFCF', white: '#FFFFFF', aqua: '#20D6FF', blue: '#2662FF',
    indigo: '#A94DFF', purple: '#A94DFF', magenta: '#E31FFF', pink: '#FF1F8F', off: '#FFFFFF'
  };
  var PALETTE = ['#FF7A00', '#FFD400', '#9BE800', '#17E85C', '#00E8A0', '#00CFCF', '#FFFFFF', '#20D6FF', '#2662FF', '#A94DFF', '#E31FFF', '#FF1F8F'];
  function hexToRgb(h) {
    h = (h || '#ffffff').replace('#', '');
    if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    var n = parseInt(h.slice(0, 6), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function mix(a, b, t) {
    var x = hexToRgb(a), y = hexToRgb(b);
    return 'rgb(' + Math.round(x[0] + (y[0] - x[0]) * t) + ',' + Math.round(x[1] + (y[1] - x[1]) * t) + ',' + Math.round(x[2] + (y[2] - x[2]) * t) + ')';
  }
  function hsl(h) { return 'hsl(' + (((h % 360) + 360) % 360) + ',100%,55%)'; }
  function col(name) { return C[name] || name; }
  // Any colour a merchant types is snapped to the nearest Crystal Color the app can show
  var LEGACY = { '#8b5cff': '#E31FFF', '#ff4fd8': '#E31FFF' };  // colours the site used before the app palette
  function snap(hex) {
    hex = col(hex);
    if (LEGACY[String(hex).toLowerCase()]) return LEGACY[String(hex).toLowerCase()];
    if (!/^#?[0-9a-f]{3,6}$/i.test(hex)) return hex;
    var a = hexToRgb(hex), best = PALETTE[10], bd = Infinity;
    PALETTE.forEach(function (p) { var b = hexToRgb(p), d = Math.pow(a[0] - b[0], 2) + Math.pow(a[1] - b[1], 2) + Math.pow(a[2] - b[2], 2); if (d < bd) { bd = d; best = p; } });
    return best;
  }

  /* ------------------------------------------------------------------ */
  /* Compass engine — geometry from the app (ringMath.ts)                */
  /* ------------------------------------------------------------------ */
  var LED = 6;           // 60 LEDs, 6° apart
  var RING_R = 161;      // circle space, puck centre (237.5, 236.55)
  var LIGHT_W = 12;
  function polar(r, deg) { var a = deg * Math.PI / 180; return [r * Math.sin(a), -r * Math.cos(a)]; }
  function arcPath(r, a1, a2) {   // clockwise sweep, 0° = 12 o'clock
    var sweep = a2 - a1; if (sweep <= 0.05) sweep = 0.05;
    if (sweep >= 359.9) sweep = 359.9;
    var p1 = polar(r, a1), p2 = polar(r, a1 + sweep);
    return 'M ' + p1[0].toFixed(2) + ' ' + p1[1].toFixed(2) + ' A ' + r + ' ' + r + ' 0 ' + (sweep > 180 ? 1 : 0) + ' 1 ' + p2[0].toFixed(2) + ' ' + p2[1].toFixed(2);
  }
  function easeOut(t) { t = clamp(t, 0, 1); return 1 - Math.pow(1 - t, 3); }
  function easeInOut(t) { t = clamp(t, 0, 1); return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  // App widths by distance: <=20 m 9 LEDs, <=40 m 5, <=100 m 3, else 1
  function widthFor(m) { return m <= 20 ? 9 : m <= 40 ? 5 : m <= 100 ? 3 : 1; }
  // Breathe (stale): 3800 ms linear, 1840 down, 60 off, 1840 up, 60 full
  function breathe(t) { var x = (t * 1000) % 3800; return x < 1840 ? 1 - x / 1840 : x < 1900 ? 0 : x < 3740 ? (x - 1900) / 1840 : 1; }

  // A frame: { arcs: [{c, a1, a2, o, cap, r}], crystal: [color, intensity], slots: [[color, o] x 8] }
  function blankFrame(own) { return { arcs: [], crystal: [own, 1], slots: [] }; }
  function arc(f, c, a1, a2, o, cap, r) { f.arcs.push({ c: col(c), a1: a1, a2: a2, o: o === undefined ? 1 : o, cap: cap || 'round', r: r || RING_R }); }
  function bond(f, c, a, leds, o, r) { var h = leds * LED / 2; arc(f, c, a - h, a + h, o, 'round', r); }
  function ring(f, c, o) { arc(f, c, 0, 359.9, o, 'butt'); }
  // Searching swirls: two comet tails 180° apart, head round-capped, tail trailing clockwise, pow 1.6 fade
  function swirl(f, c, head, tailLeds, o) {
    var deg = tailLeds * LED, segs = Math.max(8, Math.ceil(deg / 2)), step = deg / segs;
    [0, 180].forEach(function (base) {
      for (var s = 0; s < segs; s++) arc(f, c, head + base + s * step, head + base + (s + 1) * step, (o || 1) * Math.pow(1 - (s + 0.5) / segs, 1.6), s === 0 ? 'round' : 'butt');
    });
  }

  function hash(n) { var x = Math.sin(n * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); }
  var SG = [C.green, C.magenta, C.yellow, C.blue];

  // Smart Group clocks, from the video (IMG_1792): the Primary steps through the colour pairs about every 0.45 s,
  // the Totems that join flash much faster, about every 0.14 s
  var SG_SLOW = 450, SG_FAST = 140;
  function sgFill(f, t, dur) {
    var p = clamp(t / dur, 0, 1), k = Math.floor(performance.now() / SG_FAST) % 4;
    if (p > 0.004) arc(f, SG[k], 0, p * 359.9, 1, 'butt');
    f.crystal = p < 1 ? [C.white, 1] : [SG[(k + 1) % 4], 1];
  }

  var PRESETS = {
    off: function (t, f) { f.crystal = [f.own, 0]; },

    vibe: function (t, f) {
      // Sound-reactive: the Halo fills up both sides from the bottom like a level meter, indigo at the bottom blending
      // to pink at the top. Quiet moments light only the bottom few lights; loud beats reach all the way to the top.
      var beat = Math.pow(Math.abs(Math.sin(t * 4.1 + 0.6 * Math.sin(t * 1.3))), 3);
      var swell = 0.5 + 0.5 * Math.sin(t * 0.83) * Math.sin(t * 2.27 + 1);
      var jitter = 0.5 + 0.5 * Math.sin(t * 13.7) * Math.sin(t * 7.9 + 2);
      var level = clamp(0.05 + 0.5 * beat * (0.4 + 0.6 * swell) + 0.35 * swell * swell + 0.1 * jitter, 0.04, 1);
      var lit = level * 30;                       // LEDs lit on each side, 0..30 (30 = the top)
      for (var k = 0; k < 30; k++) {
        if (k >= lit) break;
        var a = k * LED, c = mix(C.indigo, C.pink, (a + 3) / 180);
        var o = clamp(lit - k, 0, 1) * (0.55 + 0.45 * (1 - k / 30));
        arc(f, c, 180 + a, 180 + a + LED, o, 'butt');
        arc(f, c, 180 - a - LED, 180 - a, o, 'butt');
      }
      f.crystal = [f.own, 1];
    },

    // Vibe Mode, with the Touch Crystal moving through the Crystal Colors (a new colour every 2.6 s, quick crossfade)
    'vibe-colors': function (t, f) {
      PRESETS.vibe(t, f);
      var L = ['#E31FFF', '#20D6FF', '#17E85C', '#FFD400', '#FF7A00', '#FF1F8F', '#A94DFF', '#00E8A0', '#2662FF', '#9BE800', '#00CFCF', '#FFFFFF'];
      var k = Math.floor(t / 2.6), x = clamp((t - k * 2.6) / 0.45, 0, 1);
      f.crystal = [mix(L[(k + L.length - 1) % L.length], L[k % L.length], x), 1];
    },

    searching: function (t, f) {
      swirl(f, f.own, -t * 60, 9);          // 6 s per revolution, counter-clockwise
    },

    locking: function (t, f) {
      // accuracy improves: tails grow 9 -> 30 LEDs until they meet, then the ring lingers and clears
      if (t < 1.4) swirl(f, f.own, -t * 60, 9 + 21 * easeOut(t / 1.4));
      else if (t < 1.75) ring(f, f.own, 0.9);
      else ring(f, f.own, 0.9 * clamp(1 - (t - 1.75) / 0.25, 0, 1));
    },

    north: function (t, f) { bond(f, C.white, Math.sin(t * 0.8) * 6, 1); },

    compass: function (t, f) {
      var w = function (s) { return Math.sin(t * 0.6 + s) * 5; };
      bond(f, C.green, 32 + w(0), 9);
      bond(f, C.aqua, 146 + w(1.3), 3);
      bond(f, C.yellow, 238 + w(2.1), 1);
      bond(f, C.pink, 300 + w(3.4), 5);
    },

    // Hero and Built to tilt: four friends, each labelled on the page (ttmx-callouts); Alex's light breathes (last seen)
    'compass-hero': function (t, f) {
      var w = function (s) { return Math.sin(t * 0.6 + s) * 5; };
      bond(f, C.green, 32 + w(0), 9);          // Sam, almost there
      bond(f, C.aqua, 146 + w(1.3), 3);        // Maya, far
      bond(f, C.yellow, 238, 1, breathe(t));   // Alex, last seen
      bond(f, C.pink, 300 + w(3.4), 5);        // Jordan, closer
    },

    'compass-turn': function (t, f) {
      var tt = Math.max(0, t - 2.2), spin = 22 * Math.sin(tt * 0.9) * Math.min(1, tt / 1.5);
      f.spin = spin;
      var w = function (s) { return Math.sin(t * 0.6 + s) * 3; };
      bond(f, C.green, 32 + w(0) - spin, 9);
      bond(f, C.aqua, 146 + w(1.3) - spin, 3);
      bond(f, C.yellow, 238 - spin, 1, breathe(t));
      bond(f, C.pink, 300 + w(3.4) - spin, 5);
    },

    // App, "Your Compass at a glance" (in time with the app story 'glance', 10.5 s loop): the satellite search with the
    // tails growing as the accuracy improves, the lock at 3.6 s, then the same Bonds the app shows (Kailey, Carter,
    // Jules last seen, the Main Stage waypoint) turning as the phone turns (4 deg/s), and Eco Mode while the app shows Eco
    glance: function (t, f) {
      var cyc = t % 10.5;
      if (cyc < 3.6) { swirl(f, f.own, -cyc * 60, 9 + 21 * easeOut(cyc / 3.6)); return; }
      if (cyc < 4.0) { ring(f, f.own, 0.9 * (1 - (cyc - 3.6) / 0.4)); }
      if (cyc >= 6.2 && cyc < 8.4) { PRESETS.eco(cyc - 6.2, f); return; }
      var h = (cyc - 3.6) * 4, k = clamp((cyc - 3.75) / 0.35, 0, 1);
      if (k <= 0) return;
      bond(f, C.magenta, 60 - h, 3, k);                       // Kailey, 62 m
      bond(f, C.green, 125 - h, 5, k);                        // Carter, 28 m
      bond(f, C.yellow, 300 - h, 1, k * breathe(cyc));        // Jules, last seen
      bond(f, Math.floor(cyc / 0.25) % 2 === 0 ? C.aqua : C.white, 205 - h, 1, k);   // Main Stage waypoint, 190 m
      f.crystal = [f.own, 1];
    },

    closer: function (t, f) {
      // a friend walks in: 150 m -> 12 m, the Bond Light widens 1 -> 3 -> 5 -> 9 LEDs (400 ms width ease)
      var cyc = t % 7, at = function (m) { return (150 - m) / 138 * 5.2; }, e = function (x) { return x <= 0 ? 0 : easeOut(x / 0.4); };
      var w = 1 + 2 * e(cyc - at(100)) + 2 * e(cyc - at(40)) + 4 * e(cyc - at(20));
      bond(f, C.pink, 22 + Math.sin(t) * 3, w, cyc > 6.4 ? 1 - (cyc - 6.4) / 0.6 : 1);
    },

    arrived: function (t, f) {
      // they're here: the Bond Light leaves the Halo and collects into the Touch Crystal
      var cyc = t % 6.4, c = C.pink, a = 24 + Math.sin(t) * 2;
      if (cyc < 1.5) { bond(f, c, a, 9); return; }
      if (cyc < 2.4) {
        var k = easeOut((cyc - 1.5) / 0.9);
        bond(f, c, a, 9 - 6 * k, 1 - k, RING_R - 92 * k);
        f.crystal = [f.own, clamp(1 - (cyc - 1.8) / 0.5, 0, 1)];
        if (cyc > 2.1) f.slots[2] = [c, (cyc - 2.1) / 0.3];
        return;
      }
      if (cyc < 5.6) { f.slots[2] = [c, 1]; return; }
      f.slots[2] = [c, 1 - (cyc - 5.6) / 0.5];
      f.crystal = [f.own, clamp((cyc - 5.9) / 0.5, 0, 1)];
    },

    nearby: function (t, f) {
      // friends within a few metres sit inside the Crystal, one slot each
      var cyc = t % 6, list = [C.pink, C.green, C.aqua];
      list.forEach(function (c, i) { var on = clamp((cyc - 0.3 - i * 0.5) / 0.3, 0, 1) * clamp((5.6 - cyc) / 0.4, 0, 1); if (on > 0) f.slots[i] = [c, on]; });
      if (cyc < 0.3 || cyc > 5.6) f.crystal = [f.own, cyc < 0.3 ? 1 - cyc / 0.3 : (cyc - 5.6) / 0.4];
    },

    pulse: function (t, f) { bond(f, C.green, 128, 3, breathe(t)); bond(f, C.yellow, 250, 1); },

    'sos-compass': function (t, f) {
      bond(f, C.pink, 58, 3, Math.floor(t / 0.2) % 2 === 0 ? 1 : 0);   // your Bond Light blinks rapidly (400 ms square wave)
      bond(f, C.aqua, 226, 3);
    },
    'sos-turn': function (t, f) {
      // SOS Alerts, Compass Mode: after a moment your friend turns to face your blinking light. The whole Compass turns
      // (f.spin, clockwise) while the lights keep pointing the same way, until the light sits in line with the Crystal's tip
      var spin = 58 * easeInOut(clamp((t - 2.6) / 1.2, 0, 1));
      f.spin = spin;
      bond(f, C.pink, 58 - spin, 3, Math.floor(t / 0.2) % 2 === 0 ? 1 : 0);
      bond(f, C.aqua, 226 - spin, 3);
    },

    'sos-vibe': function (t, f) {
      // still Vibing on the lanyard, while the Touch Crystal flashes ... --- ... in Morse code
      PRESETS.vibe(t, f);
      var pat = [1,0,1,0,1,0,0,0,1,1,1,0,1,1,1,0,1,1,1,0,0,0,1,0,1,0,1,0,0,0,0,0,0,0];
      f.crystal = [f.own, pat[Math.floor(t * 6) % pat.length] ? 1 : 0];   // in the friend's own Crystal Color, as on the next slide; fully dark between flashes
    },

    bonding: function (t, f) {
      for (var i = 0; i < 60; i++) arc(f, hsl(i * 6 + t * 220), i * LED, (i + 1) * LED, 1, 'butt');
      f.crystal = [hsl(t * 220), 1];
    },

    quickbond: function (t, f) {
      // both Crystals held together: each Halo fills up around the ring in the Bonding blue, then pulses until they match
      var p = clamp((t - 0.35) / 2.2, 0, 1);
      if (p < 1) { if (p > 0.004) arc(f, C.aqua, 0, p * 359.9, 1, 'butt'); f.crystal = [C.white, 1]; return; }
      var b = 0.35 + 0.65 * (0.5 + 0.5 * Math.cos((t - 2.55) * 3.2));
      ring(f, C.aqua, b); f.crystal = [C.white, 1];
    },

    bonded: function (t, f) {
      // Quick Bond done: the ring turns your friend's colour, both sides of the Halo retract into the top LED (at the
      // Crystal's long end), then that light collects into your Touch Crystal and stays there (the Arrived light)
      var c = f.bond || C.green;
      if (t < 1.1) { ring(f, c, 1); f.crystal = [C.white, 1]; return; }
      if (t < 2.1) {
        var h = 180 * (1 - easeOut((t - 1.1) / 1)) + LED / 2;   // half-width of the lit arc around the top
        arc(f, c, -h, h, 1, 'butt'); f.crystal = [C.white, 1]; return;
      }
      if (t < 2.8) {
        var k = easeOut((t - 2.1) / 0.7);
        bond(f, c, 0, 1, 1 - k, RING_R - 92 * k);
        f.crystal = [C.white, 1 - k];
        if (k > 0.5) f.slots[2] = [c, (k - 0.5) / 0.5];
        return;
      }
      f.slots[2] = [c, 1];
    },

    // Customize your bonds (App section, in time with the app story 'customize', 11 s loop): while Kailey's Bond is open
    // for editing, the whole Halo lights solid in her colour and the Touch Crystal blinks it; each colour tapped in the
    // app shows here at once. Before and after, her Bond Light points to her in her colour.
    'edit-bond': function (t, f) {
      var cyc = t % 11, EDIT = [[1.0, '#E31FFF'], [3.8, '#00CFCF'], [5.0, '#17E85C'], [6.2, '#FF7A00'], [7.4, '#A94DFF']], c = EDIT[0][1];
      EDIT.forEach(function (e) { if (cyc >= e[0]) c = e[1]; });
      if (cyc < 1.0 || cyc >= 8.6) {
        bond(f, c, 60, 3); bond(f, C.green, 125, 5); f.crystal = [f.own, 1]; return;
      }
      ring(f, c, 1);
      f.crystal = [c, Math.floor((cyc - 1.0) / 0.3) % 2 === 0 ? 1 : 0];
    },

    // Limitless Range (App page, with the app story 'limitless'): Mom's thin Bond Light pointing
    // north-east toward London (45°, the same bearing the app's home ring shows), live and steady the whole time
    limitless: function (t, f) {
      bond(f, C.aqua, 45, 1);
      f.crystal = [f.own, 1];
    },

    // Remote bonding (App section, in time with the app story 'remote', 11 s loop): Kailey and Carter on the Halo while
    // your friend's Bond with Me code is scanned; when the Bond is accepted (8.0 s) the Halo flashes the new friend's
    // colour once and her Bond Light joins the others.
    'remote-bond': function (t, f) {
      var cyc = t % 11, MAYA = '#FF7A00';
      bond(f, '#E31FFF', 60, 3); bond(f, C.green, 125, 5); f.crystal = [f.own, 1];
      if (cyc >= 8.0 && cyc < 9.0) { var k = (cyc - 8.0) / 1.0; ring(f, MAYA, 1 - k); f.crystal = [MAYA, 1]; }
      if (cyc >= 8.6) bond(f, MAYA, 250, 3, clamp((cyc - 8.6) / 0.4, 0, 1));
    },

    // Bonding stand-by: a Totem that isn't Bonded yet shows a plain white Touch Crystal
    idle: function (t, f) { f.crystal = [C.white, 1]; },

    locked: function (t, f) { f.crystal = [f.own, 1]; },

    calibrate: function (t, f) {
      var cyc = t % 3;
      bond(f, C.white, (t * 90) % 360, 2, 0.35);
      if (cyc > 2.4) ring(f, C.white, 1 - (cyc - 2.4) / 0.6);
    },

    update: function (t, f) {
      // software update: the Halo fills clockwise in white as the update transfers (10 s loop, in time with the app's
      // progress bar in the App section: 1.2 s -> 8.0 s), then the Crystal comes back in its colour after the reboot
      var cyc = t % 10, p = clamp((cyc - 1.2) / 6.8, 0, 1), lit = p * 60;
      if (cyc < 8.6) { if (lit > 0.05) arc(f, C.white, 0, lit * LED, 0.9, 'butt'); f.crystal = [C.white, 0.5]; return; }
      if (cyc < 9.2) { ring(f, C.white, 1 - (cyc - 8.6) / 0.6); f.crystal = [C.white, 0.2]; return; }
      f.crystal = [f.own, clamp((cyc - 9.2) / 0.4, 0, 1)];
    },

    eco: function (t, f) {
      // Eco Mode: the Halo goes dark and the Touch Crystal twinkles. Each twinkle is instant: a facet
      // flashes bright for a moment (or dips dark) and snaps straight back, like the real one
      var slot = Math.floor(t / 0.11), fac = [];
      f.crystal = [f.own, 0.62];
      for (var i = 0; i < 4; i++) {
        var h = hash(slot * 4 + i), h2 = hash(Math.floor(slot / 2) * 7 + i * 3 + 101);
        fac.push(h < 0.09 ? 1 : h < 0.14 ? 0.55 : h2 < 0.07 ? -0.85 : 0);
      }
      f.facets = fac;
    },

    'eco-upright': function (t, f) { PRESETS.eco(t, f); },   // Eco Mode while the Compass hangs upright
    'eco-tap': function (t, f) {                              // Vibe until the Power Button is tapped (1.9 s in), then Eco
      if (t < 1.9) PRESETS.vibe(t, f); else PRESETS.eco(t - 1.9, f);
    },

    // Smart Group, as the real Compass does it (IMG_1792): hold the Primary's Crystal and its ring lights aqua;
    // then Primary and every Totem that joins flash through colour pairs, ring and Crystal never the same colour
    // Smart Group: the other Totems, held one by one, go straight to rainbow: the Halo fills around the ring flashing the
    // Bonding colours (in time with the group, 500 ms a step), then keeps flashing with the rest.
    // When the Primary locks the group in, every Totem's whole Halo lights up in its own new Crystal Color.
    // the Primary, held: its Halo fills with the Bonding blue at a steady speed, then the full blue Halo pulses
    // (6 s on the real Compass; shortened here, and the page says "hold for 6 seconds"), then the rainbow begins
    'sg-hold': function (t, f) {
      var p = clamp((t - 0.3) / 1.4, 0, 1);
      f.crystal = [C.white, 1];
      if (p < 1) { if (p > 0.004) arc(f, C.aqua, 0, p * 359.9, 1, 'butt'); return; }
      if (t < 4.1) { ring(f, C.aqua, 0.4 + 0.6 * (0.5 + 0.5 * Math.cos((t - 1.7) * Math.PI * 2 / 1.2))); return; }
      PRESETS['sg-rainbow'](t, f);
    },
    'sg-rainbow': function (t, f) {
      var k = Math.floor(performance.now() / SG_SLOW) % 4;  // the Primary
      ring(f, SG[k], 1); f.crystal = [SG[(k + 1) % 4], 1];
    },
    'sg-rainbow-fast': function (t, f) {
      var k = Math.floor(performance.now() / SG_FAST) % 4;  // a Totem that has joined: the same colour pairs, much faster
      ring(f, SG[k], 1); f.crystal = [SG[(k + 1) % 4], 1];
    },
    'sg-join': function (t, f) { sgFill(f, t - 0.15, 1.05); },
    'sg-done': function (t, f) {
      // locked in: the whole Halo lights in this Totem's new Crystal Color, then goes straight to every other Totem in the
      // group collected inside the Touch Crystal (no collapse into the Halo, unlike a Quick Bond)
      var g = (f.group || []).filter(function (c) { return c !== f.own; }).slice(0, 3), SLOTS = [2, 0, 5];
      if (t < 1.4) { ring(f, f.own, clamp(t / 0.35, 0, 1)); f.crystal = [f.own, 1]; return; }
      var k = clamp((t - 1.4) / 0.45, 0, 1);
      if (k < 1) ring(f, f.own, 1 - k);
      f.crystal = [f.own, 1];
      g.forEach(function (c, i) { var on = clamp((t - 1.4 - i * 0.12) / 0.4, 0, 1); if (on > 0) f.slots[SLOTS[i]] = [c, on]; });
    },

    lowpower: function (t, f) { f.crystal = [C.red, 0.15 + 0.6 * (0.5 + 0.5 * Math.sin(t * 1.6))]; },

    // Offline event maps (App section, in time with the app story 'mapnav', 12 s loop): nothing on the Halo while the map
    // is browsed; once River Stage is sent (6.0 s) its pin sparkles on the Halo and widens as you walk to it
    'map-nav': function (t, f) {
      var cyc = t % 12; f.crystal = [f.own, 1];
      if (cyc < 6.0) return;
      var k = clamp((cyc - 6.6) / 4.4, 0, 1), w = 3 + Math.round(k * 4) * 2;
      bond(f, Math.floor(t / 0.25) % 2 === 0 ? C.aqua : C.white, 58, w);   // River Stage, up and to the right (app story 'mapnav')
    },

    waypoint: function (t, f) {
      // POI sparkle: colour 250 ms / white 250 ms
      bond(f, Math.floor(t / 0.25) % 2 === 0 ? C.aqua : C.white, 205, 3);
      bond(f, C.pink, 40, 1);
    }
  };

  var HANG = { vibe: 1, 'vibe-colors': 1, 'sos-vibe': 1, 'eco-upright': 1, 'eco-tap': 1 };

  function parseState(str) {
    // "compass" or "seq:searching:3.5,locking:1.8,compass:0"
    // or "loop:searching:2.4,locking:2,compass:2.6" (plays the steps in order, then starts over)
    str = (str || 'compass').trim();
    var loop = str.indexOf('loop:') === 0;
    if (str.indexOf('seq:') !== 0 && !loop) return [{ name: str, dur: 0 }];
    var list = str.slice(loop ? 5 : 4).split(',').map(function (s) {
      var p = s.split(':');
      return { name: p[0], dur: parseFloat(p[1] || '0') };
    });
    if (loop) list.loop = list.reduce(function (n, x) { return n + (x.dur || 0); }, 0);
    return list;
  }

  // Give every SVG id inside one instance a unique suffix so several Compasses can share a page
  var uidN = 0;
  function uniquify(el) {
    var sfx = '-' + (++uidN), map = {};
    qa(el, '[id]').forEach(function (n) { map[n.id] = n.id + sfx; n.id = n.id + sfx; });
    qa(el, '*').forEach(function (n) {
      ['filter', 'fill', 'stroke', 'mask', 'clip-path'].forEach(function (a) {
        var v = n.getAttribute(a); if (!v || v.indexOf('url(#') < 0) return;
        var id = v.slice(v.indexOf('#') + 1, v.indexOf(')'));
        if (map[id]) n.setAttribute(a, 'url(#' + map[id] + ')');
      });
      var h = n.getAttribute('href'); if (h && h.charAt(0) === '#' && map[h.slice(1)]) n.setAttribute('href', '#' + map[h.slice(1)]);
    });
  }
  var SVGNS = 'http://www.w3.org/2000/svg';

  function Compass(el) {
    this.el = el;
    uniquify(el);
    this.arcG = el.querySelector('[data-arcs]');
    this.pool = [];
    this.kite = el.querySelector('[data-kite]');
    this.kiteFill = el.querySelector('[data-kite-fill]');
    this.kiteGlow = el.querySelector('[data-kite-glow]');
    this.slots = qa(el, '[data-slot]');
    this.slotGlow = qa(el, '[data-slot-glow]');
    this.facets = qa(el, '[data-facets] path');
    this.slotStops = qa(el, '[data-slot-grad]').map(function (g) { return qa(g, 'stop'); });
    this.own = snap(el.getAttribute('data-own') || C.magenta);
    this.bond = snap(el.getAttribute('data-bond') || C.green);
    this.visible = false;
    this.set(el.getAttribute('data-state'));
    el.ttmxCompass = this;
  }
  Compass.prototype.set = function (state, opts) {
    this.seq = parseState(state);
    this.start = performance.now();
    if (opts && opts.own) this.own = snap(opts.own);
    if (opts && opts.bond) this.bond = snap(opts.bond);
    if (opts && opts.group) this.group = opts.group.map(function (c) { return snap(c.trim()); });
    this.el.setAttribute('data-state', state);
    if (reduce || !this.visible) this.draw(performance.now(), true);
  };
  Compass.prototype.draw = function (now, still) {
    var t = (now - this.start) / 1000;
    if (still) t = 2.2;
    var seq = this.seq, name = seq[seq.length - 1].name, local = t, acc = 0, si = seq.length - 1;
    if (seq.loop && !still) t = t % seq.loop;
    for (var i = 0; i < seq.length; i++) {
      if (!seq[i].dur || t < acc + seq[i].dur) { name = seq[i].name; local = t - acc; si = i; break; }
      acc += seq[i].dur;
      if (i === seq.length - 1) { name = seq[i].name; local = t - acc; }
    }
    // which step of a sequence is playing, for captions that follow it (data-seq-i on the Compass)
    if (seq.length > 1 && si !== this.si) { this.si = si; this.el.setAttribute('data-seq-i', si); }
    if (still && name === 'arrived') local = 3;
    if (still && name === 'nearby') local = 3;
    var f = blankFrame(this.own);
    f.own = this.own; f.bond = this.bond; f.group = this.group;
    (PRESETS[name] || PRESETS.compass)(local, f);
    // Vibe Mode hangs from the lanyard, so the Compass is shown the other way up
    var orient = HANG[name] ? 'hang' : 'hold';
    if (orient !== this.orient) { this.orient = orient; this.el.setAttribute('data-orient', orient); }
    paintFrame(this, f);
  };
  function paintFrame(c, f) {
    // Halo arcs from a pool of paths; the blurred <use> copy underneath mirrors them as the glow
    if (c.arcG) {
      for (var i = 0; i < f.arcs.length; i++) {
        var a = f.arcs[i], p = c.pool[i];
        if (!p) {
          p = document.createElementNS(SVGNS, 'path');
          p.setAttribute('fill', 'none'); p.setAttribute('stroke-width', LIGHT_W);
          c.arcG.appendChild(p); c.pool.push(p);
        }
        if (a.o <= 0.01) { p.style.display = 'none'; continue; }
        p.style.display = '';
        p.setAttribute('d', arcPath(a.r, a.a1, a.a2));
        p.setAttribute('stroke', a.c);
        p.setAttribute('stroke-linecap', a.cap);
        p.setAttribute('stroke-opacity', a.o.toFixed(3));
      }
      for (var j = f.arcs.length; j < c.pool.length; j++) c.pool[j].style.display = 'none';
    }
    // Crystal: the lit kite covers the render's dark crystal; hidden when off or when a bond is collected
    var anySlot = false, k;
    for (k = 0; k < 8; k++) if (f.slots[k] && f.slots[k][1] > 0.01) anySlot = true;
    var cc = col(f.crystal[0]), ci = clamp(f.crystal[1], 0, 1), lit = !anySlot && ci > 0.02;
    if (c.kite) c.kite.style.display = lit ? '' : 'none';
    if (c.kiteFill) { c.kiteFill.setAttribute('fill', cc); c.kiteFill.setAttribute('fill-opacity', (0.18 + 0.82 * ci).toFixed(3)); }
    if (c.kiteGlow) { c.kiteGlow.setAttribute('fill', cc); c.kiteGlow.setAttribute('opacity', lit ? (0.55 * ci).toFixed(3) : '0'); }
    // Eco twinkle: each facet brightens toward white or dips toward black
    for (k = 0; k < c.facets.length; k++) {
      var fv = f.facets ? f.facets[k] || 0 : 0, fp = c.facets[k];
      if (fv > 0) { fp.setAttribute('fill', mix(cc, '#ffffff', 0.72)); fp.setAttribute('opacity', (fv * 0.9).toFixed(3)); }
      else { fp.setAttribute('fill', '#000'); fp.setAttribute('opacity', (-fv * 0.55).toFixed(3)); }
    }
    // a preset can turn the whole Compass (degrees, clockwise) while its lights stay put on screen
    var sp = (f.spin || 0).toFixed(2);
    if (sp !== c.spin) { c.spin = sp; c.el.style.setProperty('--ttmx-spin', sp + 'deg'); }
    c.el.style.setProperty('--ttmx-kc', lit ? cc : '#16161b');   // off: the Crystal goes dark gray, not a dim glow
    c.el.style.setProperty('--ttmx-ki', lit ? ci.toFixed(3) : '0');
    for (k = 0; k < c.slots.length; k++) {
      var s = f.slots[k], so = s ? clamp(s[1], 0, 1) : 0;
      c.slots[k].setAttribute('opacity', so.toFixed(3));
      if (c.slotGlow[k]) { c.slotGlow[k].setAttribute('opacity', (so * 0.85).toFixed(3)); if (s) c.slotGlow[k].setAttribute('fill', col(s[0])); }
      if (s && c.slotStops[k]) c.slotStops[k].forEach(function (st, j) { st.setAttribute('stop-color', j === 0 ? mix(snap(col(s[0])), '#ffffff', 0.6) : col(s[0])); });
    }
  }
  var compasses = [];
  var rafOn = false;
  function tick(now) {
    var any = false;
    for (var i = 0; i < compasses.length; i++) {
      var c = compasses[i];
      if (!c.el.isConnected) { compasses.splice(i, 1); i--; continue; }
      if (c.visible) { c.draw(now); any = true; }
    }
    for (var m = 0; m < meshes.length; m++) {
      var me = meshes[m];
      if (!me.canvas.isConnected) { meshes.splice(m, 1); m--; continue; }
      if (me.visible) { me.frame(now); any = true; }
    }
    for (var q = 0; q < hwcalls.length; q++) {
      if (!hwcalls[q].el.isConnected) { hwcalls.splice(q, 1); q--; continue; }
      if (hwcalls[q].update()) any = true;
    }
    if (any) requestAnimationFrame(tick); else rafOn = false;
  }
  function kick() { if (!rafOn && !reduce) { rafOn = true; requestAnimationFrame(tick); } }

  var visIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var obj = e.target.ttmxCompass || e.target.ttmxMesh;
      if (!obj) return;
      obj.visible = e.isIntersecting;
      if (e.isIntersecting) { if (reduce && obj.draw) obj.draw(performance.now(), true); kick(); }
    });
  }, { rootMargin: '120px' }) : null;

  function initCompass(el) {
    if (el.ttmxCompass) return;
    var c = new Compass(el);
    compasses.push(c);
    if (visIO) visIO.observe(el); else { c.visible = true; kick(); }
    c.draw(performance.now(), true);
  }

  // 3D pieces size themselves in --u (1% of the element's width, in px)
  var uRO = 'ResizeObserver' in window ? new ResizeObserver(function (en) {
    en.forEach(function (e) { e.target.style.setProperty('--u', (e.contentRect.width / 100).toFixed(3) + 'px'); });
  }) : null;
  function initU(el) {
    if (el.ttmxU) return; el.ttmxU = true;
    el.style.setProperty('--u', (el.getBoundingClientRect().width / 100 || 4).toFixed(3) + 'px');
    if (uRO) uRO.observe(el);
  }

  /* ------------------------------------------------------------------ */
  /* Mesh demo                                                           */
  /* ------------------------------------------------------------------ */
  var meshes = [];
  function Mesh(root) {
    this.root = root;
    this.canvas = root.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.slider = root.querySelector('[data-ttmx-mesh-count]');
    this.hopsEl = root.querySelector('[data-ttmx-mesh-hops]');
    this.statusEl = root.querySelector('[data-ttmx-mesh-status]');
    this.okText = root.getAttribute('data-ok') || 'Connected in {n} hops';
    this.noText = root.getAttribute('data-no') || 'No route yet. Add more Totems.';
    this.nodes = [];
    this.path = null;
    this.packet = 0;
    this.visible = false;
    this.last = performance.now();
    var self = this;
    // Optional product photos used as node sprites (data-img-you / -friend / -relays)
    function load(src) { if (!src) return null; var im = new Image(); im.decoding = 'async'; im.src = src; return im; }
    this.imgYou = load(root.getAttribute('data-img-you'));
    this.imgFriend = load(root.getAttribute('data-img-friend'));
    this.base = load(root.getAttribute('data-img-base'));
    this.imgRelays = (root.getAttribute('data-img-relays') || '').split(',').map(function (x) { return x.trim(); }).filter(Boolean).map(load);
    this.resize();
    this.populate(parseInt(this.slider ? this.slider.value : 40, 10));
    if (this.slider) this.slider.addEventListener('input', function () { self.populate(parseInt(self.slider.value, 10), true); });
    var shuffle = root.querySelector('[data-ttmx-mesh-shuffle]');
    if (shuffle) shuffle.addEventListener('click', function () { self.populate(self.nodes.length - 2); });
    this.canvas.addEventListener('pointerdown', function (e) {
      var r = self.canvas.getBoundingClientRect();
      self.nodes.push(self.mk((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height, true));
      if (self.slider) self.slider.value = Math.min(parseInt(self.slider.max, 10), self.nodes.length - 2);
      self.route();
    });
    if ('ResizeObserver' in window) new ResizeObserver(function () { self.resize(); }).observe(this.canvas);
    else window.addEventListener('resize', function () { self.resize(); });
    root.ttmxMesh = this;
    this.canvas.ttmxMesh = this;
    if (visIO) visIO.observe(this.canvas); else { this.visible = true; kick(); }
    this.frame(performance.now());
  }
  Mesh.prototype.mk = function (x, y, fresh) {
    return { x: x, y: y, vx: (Math.random() - 0.5) * 0.004, vy: (Math.random() - 0.5) * 0.004, born: fresh ? performance.now() : 0, k: Math.floor(Math.random() * 1000), rot: (Math.random() - 0.5) * 0.6 };
  };
  // Halo geometry on a sprite of width `size`: ring r = 161/474, light 12/474 (app hero)
  // A Totem drawn the app's way: the product render (rotated 180°, stretched to a circle) with the lit kite on top
  var CROWD = ['#FFFFFF', '#FF7A00', '#FFD400', '#9BE800', '#17E85C', '#00E8A0', '#00CFCF', '#20D6FF', '#2662FF', '#A94DFF', '#E31FFF', '#FF1F8F'];   // all 12 Crystal Colors (red is not one)
  var KITE = typeof Path2D !== 'undefined' ? new Path2D('M237,117 L312,257 L237,325 L162,257 Z') : null;
  var FACETS = typeof Path2D !== 'undefined' ? new Path2D('M237,117 L237,325 M162,257 L237,236 L312,257') : null;
  // hang = Vibe Mode (worn upright on a lanyard): the Compass is the other way up, 90° corner of the Crystal on top
  Mesh.prototype.puck = function (x, y, size, color, alpha, hang) {
    var ctx = this.ctx;
    ctx.save(); ctx.globalAlpha = alpha; ctx.translate(x - size / 2, y - size / 2); ctx.scale(size / 474, size / 431);
    ctx.save(); if (!hang) { ctx.translate(474, 431); ctx.rotate(Math.PI); } ctx.drawImage(this.base, 0, 0, 474, 431); ctx.restore();
    if (KITE) {
      if (hang) { ctx.translate(474, 431); ctx.rotate(Math.PI); }
      ctx.shadowColor = color; ctx.shadowBlur = size * 0.18; ctx.fillStyle = color; ctx.fill(KITE); ctx.shadowBlur = 0;
      ctx.strokeStyle = '#07080c'; ctx.lineWidth = 7; ctx.lineJoin = 'round'; ctx.stroke(KITE); ctx.lineWidth = 5; ctx.stroke(FACETS);
    }
    ctx.restore();
  };
  Mesh.prototype.vibeArc = function (x, y, size, level, alpha) {
    // Vibe Mode: the Halo fills up both sides from the bottom with the music (level 0..1: bottom only .. the whole ring)
    var ctx = this.ctx, r = size * 0.3397, lw = Math.max(1.1, size * 0.0253), h = Math.max(0.06, level) * Math.PI;
    var g = ctx.createLinearGradient(x, y + r, x, y - r);
    g.addColorStop(0, C.indigo); g.addColorStop(1, C.pink);
    ctx.save(); ctx.globalAlpha = alpha; ctx.strokeStyle = g; ctx.lineWidth = lw; ctx.lineCap = 'butt';
    ctx.shadowColor = C.magenta; ctx.shadowBlur = size * 0.08;
    ctx.beginPath(); ctx.arc(x, y, r, Math.PI / 2 - h, Math.PI / 2 + h); ctx.stroke(); ctx.restore();
  };
  Mesh.prototype.bondArc = function (x, y, size, bearing, color, leds) {
    var ctx = this.ctx, r = size * 0.3397, lw = Math.max(1.4, size * 0.0253), a = bearing - Math.PI / 2, h = leds * 3 * Math.PI / 180;
    ctx.save(); ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.lineCap = 'round'; ctx.shadowColor = color; ctx.shadowBlur = size * 0.15;
    ctx.beginPath(); ctx.arc(x, y, r, a - h, a + h); ctx.stroke(); ctx.restore();
  };
  function ready(im) { return im && im.complete && im.naturalWidth > 0; }
  Mesh.prototype.sprite = function (im, x, y, size, alpha, rot) {
    var ctx = this.ctx;
    ctx.save(); ctx.globalAlpha = alpha; ctx.translate(x, y); if (rot) ctx.rotate(rot);
    var ar = (im.naturalWidth && im.naturalHeight) ? im.naturalWidth / im.naturalHeight : 1;
    var dw = ar >= 1 ? size : size * ar, dh = ar >= 1 ? size / ar : size;
    ctx.drawImage(im, -dw / 2, -dh / 2, dw, dh); ctx.restore();
  };
  Mesh.prototype.resize = function () {
    var r = this.canvas.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.w = Math.max(10, r.width); this.h = Math.max(10, r.height);
    this.canvas.width = this.w * dpr; this.canvas.height = this.h * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // radio range scales with the canvas diagonal so the demo behaves the same on phones
    this.R = Math.hypot(this.w, this.h) * (this.w < 600 ? 0.2 : 0.155);
  };
  Mesh.prototype.populate = function (n, keep) {
    var you = { x: 0.07, y: 0.72, fixed: true }, friend = { x: 0.93, y: 0.28, fixed: true };
    var crowd = keep ? this.nodes.slice(2) : [];
    while (crowd.length > n) crowd.pop();
    while (crowd.length < n) crowd.push(this.mk(0.04 + Math.random() * 0.92, 0.08 + Math.random() * 0.84, keep));
    this.nodes = [you, friend].concat(crowd);
    this.route(true);
  };
  // a route, once found, is kept until the message has gone all the way along it to your friend (frame() asks for a
  // fresh route only then); it's only dropped early if the crowd changes under it or one of its links drifts well out of range
  Mesh.prototype.keeps = function (path) {
    var nodes = this.nodes, R = this.R * 1.45, w = this.w, h = this.h;
    for (var i = 0; i < path.length; i++) if (path[i] >= nodes.length) return false;
    for (i = 1; i < path.length; i++) {
      var a = nodes[path[i - 1]], b = nodes[path[i]], dx = (a.x - b.x) * w, dy = (a.y - b.y) * h;
      if (dx * dx + dy * dy > R * R) return false;
    }
    return true;
  };
  Mesh.prototype.route = function (fresh) {
    if (!fresh && this.path && this.keeps(this.path)) return;
    var nodes = this.nodes, R = this.R, w = this.w, h = this.h, n = nodes.length;
    var prev = new Array(n).fill(-1), seen = new Array(n).fill(false), q = [0];
    seen[0] = true;
    while (q.length) {
      var a = q.shift();
      if (a === 1) break;
      for (var b = 0; b < n; b++) {
        if (seen[b]) continue;
        var dx = (nodes[a].x - nodes[b].x) * w, dy = (nodes[a].y - nodes[b].y) * h;
        if (dx * dx + dy * dy <= R * R) { seen[b] = true; prev[b] = a; q.push(b); }
      }
    }
    var path = null;
    if (seen[1]) { path = [1]; while (path[0] !== 0) path.unshift(prev[path[0]]); }
    // the crowd drifts, so the route keeps changing a little; the message carries on along the new route
    // (it only starts over when a route first appears), so it always makes it all the way to your friend
    var same = path && this.path && path.join() === this.path.join();
    this.path = path;
    if (!same) this.packet = 0;   // a new route: the message sets off from you
    var hops = path ? path.length - 1 : 0;
    if (this.hopsEl) this.hopsEl.textContent = path ? hops : '—';
    if (this.statusEl) {
      this.statusEl.textContent = path ? this.okText.replace('{n}', hops) : this.noText;
      if (this.hopsEl && this.hopsEl.parentNode) this.hopsEl.parentNode.classList.toggle('is-off', !path);
      this.root.setAttribute('data-connected', path ? 'true' : 'false');
    }
  };
  Mesh.prototype.frame = function (now) {
    var dt = Math.min(0.05, (now - this.last) / 1000); this.last = now;
    var ctx = this.ctx, w = this.w, h = this.h, nodes = this.nodes, R = this.R, i, j;
    // drift the crowd
    if (!reduce) {
      for (i = 2; i < nodes.length; i++) {
        var nd = nodes[i];
        nd.vx += (Math.random() - 0.5) * 0.0006; nd.vy += (Math.random() - 0.5) * 0.0006;
        nd.vx = clamp(nd.vx, -0.012, 0.012); nd.vy = clamp(nd.vy, -0.012, 0.012);
        nd.x += nd.vx * dt * 6; nd.y += nd.vy * dt * 6;
        if (nd.x < 0.03 || nd.x > 0.97) nd.vx *= -1;
        if (nd.y < 0.05 || nd.y > 0.95) nd.vy *= -1;
        nd.x = clamp(nd.x, 0.03, 0.97); nd.y = clamp(nd.y, 0.05, 0.95);
      }
      this.acc = (this.acc || 0) + dt;
      if (this.acc > 0.25) { this.acc = 0; this.route(); }
    }
    ctx.clearRect(0, 0, w, h);
    // links
    ctx.lineWidth = 1;
    for (i = 0; i < nodes.length; i++) {
      for (j = i + 1; j < nodes.length; j++) {
        var dx = (nodes[i].x - nodes[j].x) * w, dy = (nodes[i].y - nodes[j].y) * h, d2 = dx * dx + dy * dy;
        if (d2 <= R * R) {
          ctx.strokeStyle = 'rgba(255,255,255,' + (0.1 * (1 - Math.sqrt(d2) / R) + 0.02).toFixed(3) + ')';
          ctx.beginPath(); ctx.moveTo(nodes[i].x * w, nodes[i].y * h); ctx.lineTo(nodes[j].x * w, nodes[j].y * h); ctx.stroke();
        }
      }
    }
    // active route
    var path = this.path, pkt = null;
    if (path) {
      var grad = ctx.createLinearGradient(nodes[0].x * w, nodes[0].y * h, nodes[1].x * w, nodes[1].y * h);
      grad.addColorStop(0, C.magenta); grad.addColorStop(1, C.green);
      ctx.strokeStyle = grad; ctx.lineWidth = 2.5; ctx.shadowColor = 'rgba(227,31,255,.7)'; ctx.shadowBlur = 12;
      ctx.beginPath();
      path.forEach(function (k, idx) { var p = nodes[k]; if (idx) ctx.lineTo(p.x * w, p.y * h); else ctx.moveTo(p.x * w, p.y * h); });
      ctx.stroke(); ctx.shadowBlur = 0;
      // packet
      this.packet += dt * (reduce ? 0 : 3.2);
      var seg = Math.floor(this.packet), frac = this.packet - seg;
      if (seg >= path.length - 1) {
        // delivered: a green ping ripples out from your friend before the next message sets off
        var k2 = clamp((this.packet - (path.length - 1)) / 1.6, 0, 1), F = nodes[1];
        ctx.strokeStyle = 'rgba(23,232,92,' + (0.9 * (1 - k2)).toFixed(3) + ')'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.arc(F.x * w, F.y * h, 14 + 46 * k2, 0, Math.PI * 2); ctx.stroke();
        seg = path.length - 2; frac = 1;
        if (this.packet > path.length + 0.6) { this.packet = 0; this.path = null; this.route(true); }   // delivered: only now look for the next route
      }
      var A = nodes[path[seg]], B = nodes[path[seg + 1]];
      var pkt = [(A.x + (B.x - A.x) * frac) * w, (A.y + (B.y - A.y) * frac) * h];
    }
    // nodes
    var onPath = {};
    if (path) path.forEach(function (k) { onPath[k] = true; });
    var relays = this.imgRelays.filter(ready), self = this;
    var base = Math.max(22, Math.min(36, w / 24));
    var crowd = w < 600 ? base * 0.5 : base;   // phones: smaller crowd so the demo reads calmer
    for (i = 2; i < nodes.length; i++) {
      var p = nodes[i];
      var age = p.born ? clamp((now - p.born) / 600, 0, 1) : 1;
      if (relays.length || ready(this.base)) {
        var sz = (onPath[i] ? crowd * 1.35 : crowd) * (0.5 + 0.5 * age) + (1 - age) * 10;
        if (onPath[i]) { ctx.fillStyle = 'rgba(169,77,255,.3)'; ctx.beginPath(); ctx.arc(p.x * w, p.y * h, sz * 0.75, 0, Math.PI * 2); ctx.fill(); }
        if (relays.length) this.sprite(relays[p.k % relays.length], p.x * w, p.y * h, sz, onPath[i] ? 1 : 0.8, 0);
        else this.puck(p.x * w, p.y * h, sz, CROWD[p.k % CROWD.length], onPath[i] ? 1 : 0.8, true);
        // Vibe Mode: each Totem's whole Halo pulses with the music
        var ph = now / 1000 * (1.8 + (p.k % 7) * 0.12) + p.k, amp = Math.abs(Math.sin(ph) * Math.sin(ph * 0.43 + 1));
        this.vibeArc(p.x * w, p.y * h, sz, amp, onPath[i] ? 1 : 0.7);
      } else {
        ctx.fillStyle = onPath[i] ? '#ffffff' : 'rgba(255,255,255,.45)';
        if (onPath[i]) { ctx.shadowColor = 'rgba(139,92,255,.9)'; ctx.shadowBlur = 10; }
        ctx.beginPath(); ctx.arc(p.x * w, p.y * h, (onPath[i] ? 3.6 : 2.6) * (0.4 + 0.6 * age) + (1 - age) * 6, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
      }
    }
    [[0, C.magenta, this.imgYou], [1, C.green, this.imgFriend]].forEach(function (pair) {
      var nd = nodes[pair[0]], pulse = 1 + 0.25 * Math.sin(now / 300);
      ctx.strokeStyle = pair[1]; ctx.globalAlpha = 0.18; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(nd.x * w, nd.y * h, R, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1;
      if (ready(pair[2]) || ready(self.base)) {
        var big = base * 2.3;
        ctx.fillStyle = pair[1]; ctx.globalAlpha = 0.28 + 0.12 * Math.sin(now / 300);
        ctx.beginPath(); ctx.arc(nd.x * w, nd.y * h, big * 0.62 * pulse, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1;
        if (ready(pair[2])) self.sprite(pair[2], nd.x * w, nd.y * h, big, 1, 0); else self.puck(nd.x * w, nd.y * h, big, pair[1], 1);
        // Compass Mode: each one's Bond Light points at the other
        var other = nodes[pair[0] === 0 ? 1 : 0], brg = Math.atan2((other.x - nd.x) * w, -(other.y - nd.y) * h);
        self.bondArc(nd.x * w, nd.y * h, big, brg, pair[0] === 0 ? C.green : C.magenta, path ? 5 : 1);
      } else {
        ctx.fillStyle = pair[1]; ctx.shadowColor = pair[1]; ctx.shadowBlur = 22;
        ctx.beginPath(); ctx.arc(nd.x * w, nd.y * h, 7 * pulse, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
      }
    });
    if (pkt) {
      ctx.fillStyle = '#fff'; ctx.shadowColor = '#fff'; ctx.shadowBlur = 18;
      ctx.beginPath(); ctx.arc(pkt[0], pkt[1], 4.5, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
    }
  };

  /* ------------------------------------------------------------------ */
  /* Hardware callouts: an arrow points at the part that just came out  */
  /* ------------------------------------------------------------------ */
  // Each layer of the exploded view carries a tiny .ttmx-xp-pt marker at the spot the arrow should land
  // (the rim, the Crystal's corner, the GNSS chip...). Because it sits inside the 3D layer, its on-screen
  // box is exactly where that spot is drawn, however far the stack has come apart.
  // Phones: the arrow starts at the callout card. Desktop: it starts at the active item in the list.
  var hwcalls = [];
  function HwCall(el) {
    this.el = el; el.ttmxHw = this;
    this.pin = el.closest('[data-ttmx-phases]');
    this.grid = el.closest('.ttmx-hw-grid') || el.parentNode;
    this.items = qa(el, '.ttmx-hw-callouts li');
    this.list = qa(this.grid, '.ttmx-hw-item');
    this.svg = el.querySelector('.ttmx-hw-arrow');
    this.line = this.svg && this.svg.querySelector('.l');
    this.head = this.svg && this.svg.querySelector('.h');
    this.dot = this.svg && this.svg.querySelector('.d');
  }
  HwCall.prototype.update = function () {
    if (!this.svg) return false;
    var desk = window.innerWidth >= 900;
    var box = this.svg.getBoundingClientRect();
    if (box.bottom < 0 || box.top > window.innerHeight || !box.width) return false;
    var ph = parseInt(this.pin ? this.pin.getAttribute('data-phase') : '0', 10) || 0, on = null, src = null;
    this.items.forEach(function (li) { var a = parseInt(li.getAttribute('data-for-phase'), 10) === ph; li.classList.toggle('is-on', a); if (a) on = li; });
    if (desk) this.list.forEach(function (li) { if (parseInt(li.getAttribute('data-for-phase'), 10) === ph) src = li; });
    else src = on;
    this.svg.classList.toggle('is-off', !on || !src);
    if (!on || !src) return true;
    var part = on.getAttribute('data-part');
    var tgt = this.el.querySelector('.ttmx-xp-pt[data-pt="' + part + '"]');   // the 3D-render view has one marker per highlight
    if (!tgt) { var layer = this.el.querySelector('.ttmx-xp-layer[data-part="' + part + '"]'); tgt = layer && layer.querySelector('.ttmx-xp-pt'); }
    if (!tgt) return true;
    var r = tgt.getBoundingClientRect();
    var tx = r.left + r.width / 2 - box.left, ty = r.top + r.height / 2 - box.top, sx, sy;
    if (desk) {
      var sr = src.getBoundingClientRect();
      sx = sr.right - box.left + 14; sy = sr.top + Math.min(sr.height / 2, 26) - box.top;
    } else {
      var hh = on.offsetHeight || 60, v = this.el.getBoundingClientRect();
      var cy = clamp(ty + (box.top - v.top), hh / 2 + 4, v.height - hh / 2 - 4);
      on.style.setProperty('--cy', cy.toFixed(1) + 'px');
      sx = on.offsetLeft + on.offsetWidth + 4 + (v.left - box.left); sy = cy - (box.top - v.top);
    }
    var ang = Math.atan2(ty - sy, tx - sx), ex = tx - Math.cos(ang) * 8, ey = ty - Math.sin(ang) * 8;
    // a gentle curve on desktop, where the line is long
    var d = 'M' + sx.toFixed(1) + ' ' + sy.toFixed(1);
    if (desk) {
      var dir = tx >= sx ? 1 : -1; ex = tx - dir * 8; ey = ty; ang = dir > 0 ? 0 : Math.PI;
      var mx = (sx + ex) / 2;
      d += ' C' + mx.toFixed(1) + ' ' + sy.toFixed(1) + ' ' + mx.toFixed(1) + ' ' + ey.toFixed(1) + ' ' + ex.toFixed(1) + ' ' + ey.toFixed(1);
    }
    else d += ' L' + ex.toFixed(1) + ' ' + ey.toFixed(1);
    this.line.setAttribute('d', d);
    var a1 = ang + 2.6, a2 = ang - 2.6, L = 8;
    this.head.setAttribute('d', 'M' + ex.toFixed(1) + ' ' + ey.toFixed(1) + ' L' + (ex + Math.cos(a1) * L).toFixed(1) + ' ' + (ey + Math.sin(a1) * L).toFixed(1) + ' L' + (ex + Math.cos(a2) * L).toFixed(1) + ' ' + (ey + Math.sin(a2) * L).toFixed(1) + ' Z');
    this.dot.setAttribute('cx', tx.toFixed(1)); this.dot.setAttribute('cy', ty.toFixed(1));
    return true;
  };

  /* ------------------------------------------------------------------ */
  /* Chapters: side table of contents + floating CTA                     */
  /* ------------------------------------------------------------------ */
  // The nav shows once the first chapter reaches the screen and hides at the
  // closing CTA. The active chapter is the last one whose top has passed 45% of
  // the screen. On phones only the dots show; the chapter name flashes up as you
  // reach it.
  var chapters = [];
  function Chapters(el) {
    this.el = el; el.ttmxCh = this;
    this.items = qa(el, '[data-ch]').map(function (a) { return { a: a, t: document.getElementById(a.getAttribute('data-ch')) }; }).filter(function (x) { return x.t; });
    this.end = document.getElementById(el.getAttribute('data-end') || '');
    this.fab = el.querySelector('[data-ttmx-fab]');
    this.active = -1; this.flashT = null; this.lastFit = 0; this.expW = 0;
  }
  // Wide screens: the panel stays as a slim column of dots so it never opens over the page's content; the chapter
  // name floats up beside its dot as you reach it, and every name shows while the pointer is over the panel.
  // (It used to widen to show every name whenever nothing was under it, which made it pop open at awkward moments.)
  Chapters.prototype.fit = function () {
    this.el.classList.toggle('is-compact', window.innerWidth >= 1100);
  };
  Chapters.prototype.update = function () {
    if (!this.items.length) return;
    var vh = window.innerHeight, act = -1, self = this;
    this.items.forEach(function (it, i) { if (it.t.getBoundingClientRect().top <= vh * 0.45) act = i; });
    var firstTop = this.items[0].t.getBoundingClientRect().top;
    var endR = this.end ? this.end.getBoundingClientRect() : null;
    var atEnd = endR ? endR.top < vh * 0.75 : false;
    var show = firstTop <= vh * 0.6 && !atEnd;
    this.el.classList.toggle('is-shown', show);
    var now = performance.now();
    if (show && now - this.lastFit > 180) { this.lastFit = now; this.fit(); }
    if (this.fab) this.fab.classList.toggle('is-shown', firstTop <= vh * 0.6 && !atEnd);
    if (act !== this.active) {
      this.active = act;
      this.items.forEach(function (it, i) { it.a.classList.toggle('is-active', i === act); if (i === act) it.a.setAttribute('aria-current', 'true'); else it.a.removeAttribute('aria-current'); });
      this.el.classList.add('is-flash');
      clearTimeout(this.flashT);
      this.flashT = setTimeout(function () { self.el.classList.remove('is-flash'); }, 1800);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Scroll-linked bits                                                  */
  /* ------------------------------------------------------------------ */
  /* Memory: phones (iOS above all) close the tab when a page holds too many drawn layers, and this page has a lot (the 3D
     Compasses alone are ~60 layers each). Sections more than a screen away are parked with content-visibility: hidden, so
     the browser frees everything inside them; their height (content box: padding is added on top) is kept, so nothing moves. */
  var farIO = 'IntersectionObserver' in window && window.CSS && CSS.supports && CSS.supports('content-visibility', 'hidden') ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var sec = e.target;
      if (e.isIntersecting) { if (sec.hasAttribute('data-ttmx-far')) { sec.removeAttribute('data-ttmx-far'); onScroll(); } }
      else if (!sec.hasAttribute('data-ttmx-far')) { var cs = getComputedStyle(sec), h = sec.getBoundingClientRect().height - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - parseFloat(cs.borderTopWidth) - parseFloat(cs.borderBottomWidth); sec.style.containIntrinsicSize = 'auto ' + h.toFixed(2) + 'px'; sec.setAttribute('data-ttmx-far', ''); }
    });
  }, { rootMargin: '120% 0px' }) : null;
  // a new window width changes section heights, so parked sections are measured again (height-only resizes, like a
  // phone's address bar sliding away, are ignored)
  var farW = window.innerWidth, farT;
  if (farIO) window.addEventListener('resize', function () {
    if (window.innerWidth === farW) return;
    farW = window.innerWidth; clearTimeout(farT);
    farT = setTimeout(function () { qa(document, '.ttmx-section[data-ttmx-far]').forEach(function (sec) { sec.removeAttribute('data-ttmx-far'); sec.style.containIntrinsicSize = ''; farIO.unobserve(sec); farIO.observe(sec); }); }, 250);
  });

  /* The theme's header sticks to the top of the screen. Pinned sections stick just under it (--ttmx-hdr), so their kicker and
     heading are never hidden behind it; if the header slides away, the pins use the full screen again. */
  var hdrEl, hdrLast = -1, hdr0W = -1;
  function headerH() {
    if (hdrEl === undefined) hdrEl = document.querySelector('.header-section.header-sticky, .shopify-section-header-sticky, .shopify-section-group-header-group.header-section') || null;
    var h = 0;
    if (hdrEl) { var cs = getComputedStyle(hdrEl); if (cs.position === 'sticky' || cs.position === 'fixed') h = Math.max(0, Math.round(hdrEl.getBoundingClientRect().bottom)); }
    if (h > window.innerHeight * 0.3) h = 0;
    if (h !== hdrLast) { hdrLast = h; document.documentElement.style.setProperty('--ttmx-hdr', h + 'px'); }
    // --ttmx-hdr0: the header's height at the top of the page, measured once (and again if the width changes). Full-screen
    // heroes size from this, not --ttmx-hdr, so they don't grow while the announcement bar scrolls away under the header
    var vw = window.innerWidth;
    if (hdr0W !== vw && (window.pageYOffset < 4 || hdr0W === -1)) { hdr0W = vw; document.documentElement.style.setProperty('--ttmx-hdr0', h + 'px'); }
    return h;
  }

  var progressEls = [], stepGroups = [], hscrolls = [];
  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      ticking = false;
      var vh = window.innerHeight, hdr = headerH();
      progressEls = progressEls.filter(function (el) { return el.isConnected; });
      progressEls.forEach(function (el) {
        if (el.__sec === undefined) el.__sec = el.closest('.ttmx-section');
        if (el.__sec && el.__sec.hasAttribute('data-ttmx-far')) return;   // parked off screen (see farIO)
        var r = el.getBoundingClientRect(), p;
        if (el.getAttribute('data-ttmx-progress') === 'sticky') p = clamp((hdr - r.top) / Math.max(1, r.height - vh + hdr), 0, 1);   // pinned below the site's sticky header
        else p = clamp((vh - r.top) / (r.height + vh), 0, 1);
        el.style.setProperty('--p', p.toFixed(4));
        el.classList.toggle('is-view', r.top < vh * 0.85 && r.bottom > vh * 0.15);   // lets CSS start a phase's animation only once it's on screen
        var phases = parseInt(el.getAttribute('data-ttmx-phases') || '0', 10), mq = el.getAttribute('data-ttmx-mq');
        if (phases && mq && !(window.matchMedia && window.matchMedia(mq).matches)) phases = 0;
        if (phases) {
          var ph = Math.min(phases - 1, Math.floor(p * phases));
          el.classList.toggle('is-last-phase', ph === phases - 1);   // the 'Keep scrolling' hint fades out on the last step
          if (String(ph) !== el.getAttribute('data-phase')) {
            el.setAttribute('data-phase', ph);
            qa(el, '[data-phase-states]').forEach(function (c) {
              var list = c.getAttribute('data-phase-states').split(',');
              if (c.ttmxCompass && list[ph]) { var ps = list[ph].trim().split('@'); c.ttmxCompass.set(ps[0].trim(), ps[1] ? { own: ps[1].trim() } : null); } // state[@own colour]
            });
            if (el.hasAttribute('data-ttmx-tabsync')) {
              var tb = el.querySelector('[data-ttmx-tabs]');
              if (tb && tb.__tabsShow) tb.__tabsShow(ph);
            }
            qa(el, '[data-phase-focus]').forEach(function (v) {
              var list = v.getAttribute('data-phase-focus').split(','), fc = (list[ph] || 'closed').trim();
              qa(v, '[data-ttmx-xp]').forEach(function (x) {
                x.setAttribute('data-focus', fc);
                // the Halo's lights on the 3D model start over each time their part comes into focus
                var key = fc === 'closed' ? 'shell' : fc === 'halo' ? 'halo' : '';
                var hc = key && x.querySelector('.ttmx-xp-halo3d--' + key);
                if (hc && hc.ttmxCompass) hc.ttmxCompass.set(hc.getAttribute('data-seq'));
              });
            });
          }
        }
      });
      stepGroups = stepGroups.filter(function (g) { return g.el.isConnected; });
      stepGroups.forEach(function (g) {
        var line = vh * 0.5, best = 0, bestD = Infinity;
        g.steps.forEach(function (s, i) {
          var r = s.getBoundingClientRect();
          var d = r.top <= line && r.bottom >= line ? 0 : Math.min(Math.abs(r.top - line), Math.abs(r.bottom - line));
          if (d < bestD) { bestD = d; best = i; }
        });
        if (best !== g.active) activateStep(g, best);
      });
      hscrolls = hscrolls.filter(function (hs) { return hs.el.isConnected; });
      hscrolls.forEach(function (hs) { hs.update(); });
      if (hwcalls.length) { hwcalls.forEach(function (h) { h.update(); }); kick(); }
      chapters = chapters.filter(function (c) { return c.el.isConnected; });
      chapters.forEach(function (c) { c.update(); });
    });
  }

  function activateStep(g, i) {
    g.active = i;
    g.el.setAttribute('data-active', i);
    g.steps.forEach(function (s, k) { s.classList.toggle('is-active', k === i); });
    qa(g.el, '[data-ttmx-panel]').forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-ttmx-panel') === String(i)); });
    qa(g.el, '[data-ttmx-dot]').forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-ttmx-dot') === String(i)); });
    var st = g.steps[i] && g.steps[i].getAttribute('data-ttmx-states');
    if (st) applyStates(g.el, st);
    var fc = g.steps[i] && g.steps[i].getAttribute('data-ttmx-focus');
    if (fc !== null && fc !== undefined) qa(g.el, '.ttmx-story-stage [data-ttmx-xp]').forEach(function (x) { x.setAttribute('data-focus', fc || 'closed'); });
  }

  function applyStates(scope, list) {
    var states = list.split('|');
    qa(scope, '[data-ttmx-compass]').forEach(function (c, k) {
      var s = states[Math.min(k, states.length - 1)];
      if (c.ttmxCompass && s) {
        var parts = s.split('@'); // state@owncolour[@group colours, comma separated]
        c.ttmxCompass.set(parts[0].trim(), parts[1] ? { own: parts[1].trim(), group: parts[2] ? parts[2].split(',') : null } : null);
      }
    });
  }

  function HScroll(el) {
    this.el = el;
    this.track = el.querySelector('[data-ttmx-hscroll-track]');
    this.el.ttmxH = this;
    this.measure();
    var self = this;
    window.addEventListener('resize', function () { self.measure(); onScroll(); });
  }
  HScroll.prototype.measure = function () {
    var mq = window.innerWidth >= 900 && !reduce;
    this.on = mq;
    this.el.classList.toggle('is-pinned', mq);
    if (!mq) { this.el.style.height = ''; this.track.style.transform = ''; return; }
    this.dist = Math.max(0, this.track.scrollWidth - this.track.parentNode.clientWidth);
    this.el.style.height = (window.innerHeight + this.dist) + 'px';
  };
  HScroll.prototype.update = function () {
    if (!this.on) return;
    var r = this.el.getBoundingClientRect();
    var p = clamp(-r.top / Math.max(1, r.height - window.innerHeight), 0, 1);
    this.track.style.transform = 'translate3d(' + (-p * this.dist).toFixed(1) + 'px,0,0)';
    this.el.style.setProperty('--p', p.toFixed(4));
  };

  /* ------------------------------------------------------------------ */
  /* Reveal + counters                                                   */
  /* ------------------------------------------------------------------ */
  var revealIO = 'IntersectionObserver' in window ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      if (e.target.hasAttribute('data-ttmx-count')) runCount(e.target);
      qa(e.target, '[data-ttmx-count]').forEach(runCount);
      revealIO.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 }) : null;

  function runCount(el) {
    if (el.__counted) return; el.__counted = true;
    var raw = el.getAttribute('data-ttmx-count');
    var target = parseFloat(raw.replace(/[^0-9.]/g, ''));
    if (isNaN(target)) return;
    var dec = (raw.split('.')[1] || '').replace(/[^0-9]/g, '').length;
    if (reduce) { el.textContent = fmt(target, dec); return; }
    var t0 = performance.now(), dur = 1400;
    (function step(now) {
      var k = clamp((now - t0) / dur, 0, 1), e = 1 - Math.pow(1 - k, 3);
      el.textContent = fmt(target * e, dec);
      if (k < 1) requestAnimationFrame(step);
    })(t0);
  }
  function fmt(v, dec) { return v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }); }

  /* ------------------------------------------------------------------ */
  /* Tabs                                                                */
  /* ------------------------------------------------------------------ */
  function initTabs(el) {
    if (el.__tabs) return; el.__tabs = true;
    var tabs = qa(el, '[data-ttmx-tab]').filter(function (t) { return t.closest('[data-ttmx-tabs]') === el; });
    var auto = parseInt(el.getAttribute('data-ttmx-autoplay') || '0', 10);
    var timer = null, current = -1;
    function show(i, user) {
      current = i;
      tabs.forEach(function (t, k) {
        var on = k === i;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      qa(el, '[data-ttmx-tabpanel]').forEach(function (p) {
        if (p.closest('[data-ttmx-tabs]') !== el) return;
        var on = p.getAttribute('data-ttmx-tabpanel') === String(i);
        p.classList.toggle('is-active', on);
        p.hidden = !on;
      });
      var st = tabs[i] && tabs[i].getAttribute('data-ttmx-states');
      if (st) applyStates(el, st);
      var v = tabs[i] && tabs[i].getAttribute('data-ttmx-v');   // lets CSS stage extras (e.g. a finger) per step
      if (v) el.setAttribute('data-v', v);
      if (user && timer) { clearTimeout(timer); timer = null; el.classList.add('is-manual'); }
    }
    tabs.forEach(function (t, k) {
      t.addEventListener('click', function () { show(k, true); });
      t.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && t.tagName !== 'BUTTON') { e.preventDefault(); show(k, true); return; }
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); var n = (k + 1) % tabs.length; tabs[n].focus(); show(n, true); }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') { e.preventDefault(); var p = (k - 1 + tabs.length) % tabs.length; tabs[p].focus(); show(p, true); }
      });
    });
    el.__tabsShow = function (i) { if (i !== current && tabs[i]) show(i); };
    show(0);
    // a phone scroll pin drives the tabs instead of the timer
    var pin = el.closest('[data-ttmx-mq]');
    function pinned() { return pin && window.matchMedia && window.matchMedia(pin.getAttribute('data-ttmx-mq')).matches; }
    if (auto && !reduce && tabs.length > 1) {
      var inView = false;
      if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { inView = en[0].isIntersecting; }).observe(el);
      // each tab can ask for longer with data-ttmx-dur (ms), e.g. a 6 s press-and-hold
      var step = function () {
        var d = parseInt((tabs[current] && tabs[current].getAttribute('data-ttmx-dur')) || auto, 10) || auto;
        timer = setTimeout(function () { if (!timer) return; if (inView && !pinned()) show((current + 1) % tabs.length); if (timer) step(); }, d);
      };
      step();
    }
  }


  /* ------------------------------------------------------------------ */
  /* Glossary tooltips                                                   */
  /* ------------------------------------------------------------------ */
  // Terms come from a JSON block rendered by the "Totem glossary" section:
  // [{ "term": "GNSS", "match": ["GNSS"], "text": "..." }, ...]
  // The first mention of each term inside each .ttmx-section gets a "?" tip.
  var glossary = null, tipSeq = 0;
  function loadGlossary() {
    if (glossary) return glossary;
    glossary = [];
    qa(document, 'script[data-ttmx-glossary]').forEach(function (el) {
      try { glossary = glossary.concat(JSON.parse(el.textContent) || []); } catch (e) {}
    });
    glossary.forEach(function (g) {
      var words = (g.match && g.match.length ? g.match : [g.term]).filter(Boolean).map(function (w) {
        return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }).sort(function (a, b) { return b.length - a.length; });
      g.re = words.length ? new RegExp('(^|[^\\w])(' + words.join('|') + ')(?![\\w])', 'i') : null;
    });
    return glossary;
  }
  var SKIP = 'a, button, summary, h1, h2, .heading, script, style, svg, canvas, textarea, input, select, h3, label, dt, figcaption, .ttmx-kicker, .ttmx-step-kicker, .ttmx-pill, .ttmx-legend, .ttmx-stat, [data-ttmx-noglossary], .ttmx-term, .ttmx-scr, [role="tab"]';
  function wrapTerm(section, g) {
    var walker = document.createTreeWalker(section, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        if (!n.nodeValue || !g.re.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
        var p = n.parentElement;
        if (!p || p.closest(SKIP)) return NodeFilter.FILTER_REJECT;
        if (p.offsetParent === null && getComputedStyle(p).position !== 'fixed') return NodeFilter.FILTER_REJECT; // hidden copies (mobile/desktop duplicates)
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var node = walker.nextNode();
    if (!node) return;
    var m = node.nodeValue.match(g.re);
    var start = m.index + m[1].length, word = m[2];
    var after = node.splitText(start);
    after.nodeValue = after.nodeValue.slice(word.length);
    var id = 'ttmx-tip-' + (++tipSeq);
    var wrap = document.createElement('span');
    wrap.className = 'ttmx-term';
    wrap.innerHTML = '<span class="ttmx-term-word"></span><button type="button" class="ttmx-tip" aria-expanded="false" aria-describedby="' + id + '"><span aria-hidden="true">?</span><span class="ttmx-sr"></span></button><span class="ttmx-tip-pop" role="tooltip" id="' + id + '"><strong></strong><span></span></span>';
    wrap.querySelector('.ttmx-term-word').textContent = word;
    wrap.querySelector('.ttmx-sr').textContent = 'What is ' + g.term + '?';
    wrap.querySelector('.ttmx-tip-pop strong').textContent = g.term;
    wrap.querySelector('.ttmx-tip-pop span').textContent = g.text;
    node.parentNode.insertBefore(wrap, after);
  }
  function placeTip(term) {
    var pop = term.querySelector('.ttmx-tip-pop');
    pop.style.setProperty('--shift', '0px');
    term.classList.remove('is-below');
    var r = pop.getBoundingClientRect(), pad = 12, shift = 0;
    if (r.left < pad) shift = pad - r.left;
    else if (r.right > window.innerWidth - pad) shift = window.innerWidth - pad - r.right;
    pop.style.setProperty('--shift', shift.toFixed(0) + 'px');
    if (r.top < 70) term.classList.add('is-below');
  }
  function openTip(term, on) {
    qa(document, '.ttmx-term.is-open').forEach(function (t) { if (t !== term) { t.classList.remove('is-open'); t.querySelector('.ttmx-tip').setAttribute('aria-expanded', 'false'); } });
    term.classList.toggle('is-open', on);
    term.querySelector('.ttmx-tip').setAttribute('aria-expanded', on ? 'true' : 'false');
    if (on) placeTip(term);
  }
  function initGlossary(root) {
    var list = loadGlossary();
    if (!list.length) return;
    var sections = root.classList && root.classList.contains('ttmx-section') ? [root] : qa(root, '.ttmx-section');
    sections.forEach(function (sec) {
      if (sec.__gloss) return; sec.__gloss = true;
      list.forEach(function (g) { if (g.re && g.text) wrapTerm(sec, g); });
    });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.ttmx-tip');
    if (btn) { e.preventDefault(); e.stopPropagation(); var term = btn.closest('.ttmx-term'); openTip(term, !term.classList.contains('is-open')); return; }
    if (!(e.target.closest && e.target.closest('.ttmx-tip-pop'))) qa(document, '.ttmx-term.is-open').forEach(function (t) { openTip(t, false); });
  }, true);
  document.addEventListener('mouseover', function (e) {
    var term = e.target.closest && e.target.closest('.ttmx-term');
    if (term && window.matchMedia('(hover: hover)').matches) placeTip(term);
  });
  document.addEventListener('focusin', function (e) {
    var term = e.target.closest && e.target.closest('.ttmx-term');
    if (term) placeTip(term);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') qa(document, '.ttmx-term.is-open').forEach(function (t) { openTip(t, false); });
  });

  /* ------------------------------------------------------------------ */
  /* Photo stack                                                          */
  /* ------------------------------------------------------------------ */
  // Cards fly in one by one when the stack scrolls into view, then the top
  // card is tossed to the back of the pile on a timer.
  function Stack(el) {
    this.el = el; el.ttmxStack = this;
    this.cards = qa(el, '[data-ttmx-card]');
    this.interval = parseInt(el.getAttribute('data-interval') || '3500', 10);
    this.order = this.cards.map(function (c, i) { return i; });
    this.started = false;
    var self = this;
    this.layout();
    if (reduce) { this.cards.forEach(function (c) { c.classList.add('is-in'); }); return; }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) {
        self.visible = en[0].isIntersecting;
        if (self.visible && !self.started) self.start();
      }, { threshold: 0.3 }).observe(el);
    } else this.start();
    el.addEventListener('pointerenter', function () { self.paused = true; });
    el.addEventListener('pointerleave', function () { self.paused = false; });
    el.addEventListener('click', function () { self.next(true); });
  }
  Stack.prototype.layout = function () {
    var n = this.order.length;
    this.order.forEach(function (ci, pos) {
      var c = this.cards[ci];
      c.style.zIndex = String(n - pos);
      c.style.setProperty('--pos', pos);
      c.classList.toggle('is-top', pos === 0);
    }, this);
  };
  Stack.prototype.start = function () {
    var self = this; this.started = true;
    // deal from the bottom of the pile up so the top photo lands last
    var deal = this.order.slice().reverse();
    deal.forEach(function (ci, k) { setTimeout(function () { self.cards[ci].classList.add('is-in'); }, 160 + k * 230); });
    setTimeout(function () {
      self.timer = setInterval(function () { if (self.visible && !self.paused && document.visibilityState !== 'hidden') self.next(); }, self.interval);
    }, 160 + deal.length * 230 + 600);
  };
  Stack.prototype.next = function (user) {
    if (!this.started || this.busy || this.order.length < 2) return;
    var self = this, top = this.cards[this.order[0]];
    this.busy = true;
    top.classList.add('is-out');
    setTimeout(function () {
      self.order.push(self.order.shift());
      top.classList.remove('is-out');
      self.layout();
      self.busy = false;
    }, 520);
  };


  /* ------------------------------------------------------------------ */
  /* Hero background: the Totem mesh network, animated                   */
  /* ------------------------------------------------------------------ */
  // Same rules as the brand's mesh art (mesh-background generator handoff): nodes scattered with a minimum gap, linked
  // when closer than link_dist with brightness by closeness, pink and white, two magenta "hero" nodes with ping rings,
  // a soft bloom. Here the nodes drift on small closed loops, messages hop node to node, and the ping rings expand.
  // Density follows the canvas area the same way (91 nodes and 303 px links at 1920 x 1080, exponents .72 and .30).
  function MeshBg(cv) {
    this.cv = cv; this.ctx = cv.getContext('2d'); cv.ttmxMesh = this;
    this.seed = parseInt(cv.getAttribute('data-seed') || '11', 10);
    // data-palette='app': the App page's indigo version. One hub (the phone) instead of two hero nodes, Bluetooth-style
    // rings around it, and messages that route node by node toward the hub with a short trail, flashing it as they land
    this.app = cv.getAttribute('data-palette') === 'app';
    // data-tone='pink' (App page): the same hub mesh in Totem pinks
    this.tone = cv.getAttribute('data-tone') || (this.app ? 'indigo' : 'pink');
    this.pal = this.app && this.tone === 'indigo' ? { a: '167,139,255', b: '214,226,255', node: 'rgba(139,92,255,.98)', glow: '#8B5CFF', pkt: '196,181,253', pglow: '#7C5CFF', ring: '139,92,255' }
                        : { a: '241,153,235', b: '255,255,255', node: 'rgba(242,60,212,.96)', glow: '#F23CD4', pkt: '255,122,226', pglow: '#F23CD4', ring: '255,122,226' };
    this.flash = 0;
    this.visible = true; this.t0 = performance.now(); this.packets = [];
    var self = this;
    this.resize();
    window.addEventListener('resize', function () { clearTimeout(self.rt); self.rt = setTimeout(function () { self.resize(); }, 150); });
    if ('IntersectionObserver' in window) new IntersectionObserver(function (en) { self.visible = en[0].isIntersecting; if (self.visible) self.kick(); }, { rootMargin: '100px' }).observe(cv);
    this.kick();
  }
  MeshBg.prototype.rand = function () { this.s = (this.s * 16807) % 2147483647; return (this.s - 1) / 2147483646; };
  MeshBg.prototype.resize = function () {
    var r = this.cv.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    this.w = Math.max(10, r.width); this.h = Math.max(10, r.height);
    this.cv.width = Math.round(this.w * dpr); this.cv.height = Math.round(this.h * dpr); this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var area = (this.w * this.h) / (1920 * 1080), n = Math.max(30, Math.round(91 * Math.pow(area, 0.72)));
    this.L = 303 * Math.pow(area, 0.30) * (this.w < 700 ? 0.82 : 1);
    this.s = this.seed * 7919 + 13;
    var pts = [], tries = 0, gap = this.L * 0.44;
    while (pts.length < n && tries < n * 240) {
      tries++;
      var x = (-0.04 + 1.08 * this.rand()) * this.w, y = (-0.04 + 1.08 * this.rand()) * this.h, ok = true;
      for (var i = 0; i < pts.length; i++) { var dx = x - pts[i].x, dy = y - pts[i].y; if (dx * dx + dy * dy < gap * gap) { ok = false; break; } }
      if (ok) pts.push({ x: x, y: y });
    }
    var self = this;
    pts.forEach(function (p) {
      p.ax = 6 + 14 * self.rand(); p.ay = 6 + 14 * self.rand(); p.ph = 6.283 * self.rand(); p.sp = 0.12 + 0.12 * self.rand();
      p.pink = self.rand() < 0.5; p.r = (1.6 + 1.1 * self.rand()) * 1.26; p.a = Math.min(0.94, (0.39 + 0.28 * self.rand()) * 1.6);
    });
    // the two hero nodes, top right and bottom left, with ping rings
    this.hero = [{ x: this.w * 0.83, y: this.h * 0.20, ax: 0, ay: 0, ph: 0, sp: 0, hero: 1, r0: 30, step: 28, n: 5, col: '241,153,235' },
                 { x: this.w * 0.14, y: this.h * 0.83, ax: 0, ay: 0, ph: 0, sp: 0, hero: 1, r0: 24, step: 24, n: 4, col: '242,60,212' }];
    if (this.app) {
      var hub = ((this.w < 750 && this.cv.getAttribute('data-hub-m')) || this.cv.getAttribute('data-hub') || '0.72,0.5').split(',');
      this.hero = [{ x: this.w * parseFloat(hub[0]), y: this.h * parseFloat(hub[1]), ax: 0, ay: 0, ph: 0, sp: 0, hero: 1, r0: this.w < 750 ? 60 : 120, step: this.w < 750 ? 30 : 46, n: 6, col: this.pal.ring }];
      // the nodes right under the phone would only clutter it: clear a circle round the hub
      var hx = this.hero[0].x, hy = this.hero[0].y, clr = this.hero[0].r0 * 0.9;
      pts = pts.filter(function (p) { return Math.hypot(p.x - hx, p.y - hy) > clr; });
      pts.forEach(function (p) { p.tw = self.rand() < 0.22 ? 0.6 + 1.6 * self.rand() : 0; });
    }
    this.pts = pts.concat(this.hero);
    // links are fixed pairs (by rest position); colour per link
    this.links = [];
    for (var a = 0; a < this.pts.length; a++) for (var b = a + 1; b < this.pts.length; b++) {
      var P = this.pts[a], Q = this.pts[b], d = Math.hypot(P.x - Q.x, P.y - Q.y);
      if (d < this.L * (this.app && (P.hero || Q.hero) ? 1.5 : 1.08)) this.links.push({ a: a, b: b, pink: this.rand() < 0.45, hub: this.app && (P.hero || Q.hero) });
    }
    this.nb = this.pts.map(function () { return []; });
    this.links.forEach(function (l) { self.nb[l.a].push(l.b); self.nb[l.b].push(l.a); });
    this.packets = [];
    if (reduce) this.draw(this.t0 + 4000);
  };
  // the neighbour that gets a message closest to the hub (none if no neighbour is closer than here)
  MeshBg.prototype.toHub = function (i) {
    var H = this.pts[this.pts.length - 1], best, bd = Math.hypot(this.pts[i].x - H.x, this.pts[i].y - H.y), self = this;
    this.nb[i].forEach(function (n) { var d = Math.hypot(self.pts[n].x - H.x, self.pts[n].y - H.y); if (d < bd - 1) { bd = d; best = n; } });
    return best;
  };
  MeshBg.prototype.pos = function (p, t) { return p.hero ? [p.x, p.y] : [p.x + Math.cos(t * p.sp + p.ph) * p.ax, p.y + Math.sin(t * p.sp * 1.3 + p.ph) * p.ay]; };
  MeshBg.prototype.kick = function () {
    if (reduce || this.on) return; this.on = true; var self = this;
    var loop = function (now) { if (!self.visible || !self.cv.isConnected) { self.on = false; return; } self.draw(now); requestAnimationFrame(loop); };
    requestAnimationFrame(loop);
  };
  MeshBg.prototype.draw = function (now) {
    var ctx = this.ctx, t = (now - this.t0) / 1000, w = this.w, h = this.h, L = this.L, self = this;
    ctx.clearRect(0, 0, w, h);
    var P = this.pts.map(function (p) { return self.pos(p, t); });
    // ping rings: concentric, fading outward, slowly expanding
    this.hero.forEach(function (hp, k) {
      for (var i = 0; i < hp.n; i++) {
        var f = ((t * (self.app ? 0.24 : 0.18) + k * 0.5) % 1), r = hp.r0 + (i + f) * hp.step, a = (self.app ? 0.3 + 0.35 * self.flash : 0.37) * (1 - (i + f) / (hp.n + 0.9));
        if (a <= 0) continue;
        ctx.strokeStyle = 'rgba(' + hp.col + ',' + a.toFixed(3) + ')'; ctx.lineWidth = 1.7;
        ctx.beginPath(); ctx.arc(hp.x, hp.y, r, 0, 6.2832); ctx.stroke();
      }
    });
    // links
    ctx.lineWidth = 1.65;
    for (var i = 0; i < this.links.length; i++) {
      var l = this.links[i], A = P[l.a], B = P[l.b], d = Math.hypot(A[0] - B[0], A[1] - B[1]);
      if (d >= (l.hub ? L * 1.5 : L)) continue;
      var LL = l.hub ? L * 1.5 : L;
      if (l.hub && d >= LL) continue;
      var a = 0.38 * Math.pow(1 - d / LL, 1.05) * (l.hub ? 0.8 : 1);
      if (a < 0.016) continue;
      ctx.strokeStyle = 'rgba(' + (l.pink ? this.pal.a : this.pal.b) + ',' + a.toFixed(3) + ')';
      ctx.beginPath(); ctx.moveTo(A[0], A[1]); ctx.lineTo(B[0], B[1]); ctx.stroke();
    }
    // nodes
    for (var j = 0; j < this.pts.length; j++) {
      var p = this.pts[j], q = P[j];
      if (p.hero) { if (this.app) continue; ctx.fillStyle = this.pal.node; ctx.shadowColor = this.pal.glow; ctx.shadowBlur = 12; ctx.beginPath(); ctx.arc(q[0], q[1], 4, 0, 6.2832); ctx.fill(); ctx.shadowBlur = 0; continue; }
      var na = p.tw ? p.a * (0.35 + 0.65 * Math.pow(0.5 + 0.5 * Math.sin(t * p.tw + p.ph), 2)) : p.a;
      ctx.fillStyle = 'rgba(' + (p.pink ? this.pal.a : this.pal.b) + ',' + na.toFixed(3) + ')';
      ctx.beginPath(); ctx.arc(q[0], q[1], p.r, 0, 6.2832); ctx.fill();
    }
    // messages hopping across the mesh: a glowing dot runs along a link, then on to a neighbour (5 or 6 hops)
    this.flash = Math.max(0, this.flash - 0.02);
    if (this.app) {
      // messages start somewhere out in the mesh and route toward the hub, one hop at a time
      if (this.packets.length < 4 && Math.random() < 0.03) {
        var hb = this.pts.length - 1, H = this.pts[hb], far = [];
        for (var u = 0; u < this.pts.length - 1; u++) if (Math.hypot(this.pts[u].x - H.x, this.pts[u].y - H.y) > Math.min(w, h) * 0.45) far.push(u);
        var s0 = far[Math.floor(Math.random() * far.length)];
        var nxt = s0 !== undefined ? this.toHub(s0) : undefined;
        if (nxt !== undefined) this.packets.push({ from: s0, to: nxt, t0: t, hops: 40, hub: 1 });
      }
    } else if (this.packets.length < 3 && Math.random() < 0.02) {
      var st = Math.random() < 0.5 ? this.pts.length - 2 : this.pts.length - 1;
      this.packets.push({ from: st, to: this.nb[st][Math.floor(Math.random() * this.nb[st].length)], t0: t, hops: 5 + Math.floor(Math.random() * 3) });
    }
    this.packets = this.packets.filter(function (pk) {
      if (pk.to === undefined) return false;
      var A = P[pk.from], B = P[pk.to], dur = Math.max(0.5, Math.hypot(A[0] - B[0], A[1] - B[1]) / 260), k = (t - pk.t0) / dur;
      if (k >= 1) {
        pk.hops--; if (pk.hops <= 0) return false;
        if (pk.hub) {
          if (self.pts[pk.to].hero) { self.flash = 1; return false; }
          var hn = self.toHub(pk.to);
          if (hn === undefined) return false;
          pk.from = pk.to; pk.to = hn;
        } else {
          var nx = self.nb[pk.to].filter(function (n) { return n !== pk.from; });
          if (!nx.length) return false;
          pk.from = pk.to; pk.to = nx[Math.floor(Math.random() * nx.length)];
        }
        pk.t0 = t; k = 0; A = P[pk.from]; B = P[pk.to];
      }
      var x = A[0] + (B[0] - A[0]) * k, y = A[1] + (B[1] - A[1]) * k;
      ctx.shadowColor = self.pal.pglow; ctx.shadowBlur = 14;
      if (pk.hub) for (var tr = 3; tr >= 1; tr--) {   // a short fading trail behind it
        var kk = Math.max(0, k - tr * 0.07); ctx.fillStyle = 'rgba(' + self.pal.pkt + ',' + (0.5 - tr * 0.13).toFixed(2) + ')';
        ctx.beginPath(); ctx.arc(A[0] + (B[0] - A[0]) * kk, A[1] + (B[1] - A[1]) * kk, 2.6 - tr * 0.45, 0, 6.2832); ctx.fill();
      }
      ctx.fillStyle = 'rgba(' + self.pal.pkt + ',.95)';
      ctx.beginPath(); ctx.arc(x, y, 2.6, 0, 6.2832); ctx.fill(); ctx.shadowBlur = 0;
      return true;
    });
  };

  /* ------------------------------------------------------------------ */
  /* Init                                                                */
  /* ------------------------------------------------------------------ */
  /* Scene Vibe: the Totems in the tech scenes Vibe exactly like the ones in Try the mesh (same level curve per Totem,
     same Halo fill from the bottom) — each .ttmx-svibe circle (pathLength 100) shows a dash centred on the bottom */
  var svibes = [], svOn = false;
  function svTick(now) {
    var vh = window.innerHeight, seen = [];
    svibes = svibes.filter(function (s) { return s.c.isConnected; });
    svibes.forEach(function (s) {
      if (seen.indexOf(s.svg) < 0) { var r = s.svg.getBoundingClientRect(); s.svg.__svIn = r.width > 0 && r.bottom > 0 && r.top < vh; seen.push(s.svg); }
      if (!s.svg.__svIn) return;
      var ph = now / 1000 * (1.8 + (s.k % 7) * 0.12) + s.k, amp = Math.abs(Math.sin(ph) * Math.sin(ph * 0.43 + 1));
      var L = 100 * Math.max(0.06, amp);
      s.c.style.strokeDasharray = L.toFixed(2) + ' ' + Math.max(0.01, 100 - L).toFixed(2); s.c.style.strokeDashoffset = (L / 2 - 25).toFixed(2);   // period 100, so the fill stays centred on the bottom and grows up both sides evenly
    });
    if (svibes.length) requestAnimationFrame(svTick); else svOn = false;
  }

  /* ------------------------------------------------------------------ */
  /* Totems beside phones: true to scale                                 */
  /* ------------------------------------------------------------------ */
  // Wherever a Compass sits beside a phone (How it Works app section; App page hero, stories and app CTA), it's sized
  // from that phone so the two are in real proportion: a Totem Compass is 58 mm across, an iPhone is about 71.5 mm wide,
  // so the Totem is 0.81 of the phone's width. Measured with offsetWidth (ignores the phones' tilt), kept up to date
  // as the phone resizes.
  var TOTEM_PER_PHONE = 58 / 71.5;
  var PAIR = '.ttmx-appsum-compass, .ttmx-ah-compass, .ttmx-st-compass, .ttmx-swc-compass';
  var pairRO = 'ResizeObserver' in window ? new ResizeObserver(function (en) { en.forEach(function (e) { if (e.target.__pairs) e.target.__pairs.forEach(sizePair); }); }) : null;
  function sizePair(c) {
    var ph = c.__phone; if (!ph) return;
    var w = ph.offsetWidth; if (!w) return;
    c.style.width = (w * TOTEM_PER_PHONE).toFixed(1) + 'px'; c.style.maxWidth = 'none';
    if (c.parentElement) c.parentElement.style.setProperty('--ttmx-cw', (w * TOTEM_PER_PHONE).toFixed(1) + 'px');   // things placed against the Totem (the app tooltip)
  }
  function initPair(c) {
    if (c.__phone) return;
    var box = c.parentElement, ph = null;
    while (box && !ph && !box.classList.contains('shopify-section')) { ph = box.querySelector('.ttmx-phone'); box = box.parentElement; }
    if (!ph) return;
    c.__phone = ph; (ph.__pairs = ph.__pairs || []).push(c);
    sizePair(c);
    if (pairRO) pairRO.observe(ph); else window.addEventListener('resize', function () { sizePair(c); });
  }

  function init(root) {
    qa(root, '.ttmx-svibe').forEach(function (c, i) {
      if (c.__sv) return; c.__sv = true; var svg = c.ownerSVGElement || c;
      svibes.push({ c: c, k: i + 3, svg: svg });
    });
    if (!svOn && svibes.length && !reduce) { svOn = true; requestAnimationFrame(svTick); }
    root = root || document;
    qa(root, '[data-ttmx-compass]').forEach(initCompass);
    qa(root, '[data-ttmx-u]').forEach(initU);
    qa(root, '[data-ttmx-hwcall]').forEach(function (el) { if (!el.ttmxHw) hwcalls.push(new HwCall(el)); });
    qa(root, 'svg.ttmx-scene').forEach(function (sv) { if (!sv.ttmxU) { sv.ttmxU = true; uniquify(sv); } });
    var vh0 = window.innerHeight;
    qa(root, '[data-ttmx-reveal]').forEach(function (el) {
      if (reduce || !revealIO) { el.classList.add('is-in'); return; }
      var r = el.getBoundingClientRect();
      // Anything already on screen at load animates in straight away.
      if (r.top < vh0 && r.bottom > 0 && r.width) {
        requestAnimationFrame(function () { el.classList.add('is-in'); qa(el, '[data-ttmx-count]').forEach(runCount); if (el.hasAttribute('data-ttmx-count')) runCount(el); });
      } else revealIO.observe(el);
    });
    qa(root, '[data-ttmx-count]').forEach(function (el) {
      if (!el.closest('[data-ttmx-reveal]')) { if (revealIO) revealIO.observe(el); else runCount(el); }
    });
    qa(root, '[data-ttmx-progress]').forEach(function (el) { if (progressEls.indexOf(el) < 0) progressEls.push(el); });
    qa(root, '[data-ttmx-steps]').forEach(function (el) {
      if (el.__steps) return; el.__steps = true;
      var g = { el: el, steps: qa(el, '[data-ttmx-step]'), active: -1 };
      stepGroups.push(g);
      activateStep(g, 0);
      qa(el, '[data-ttmx-dot]').forEach(function (d) {
        d.addEventListener('click', function () {
          var s = g.steps[parseInt(d.getAttribute('data-ttmx-dot'), 10)];
          if (s) window.scrollTo({ top: s.getBoundingClientRect().top + window.pageYOffset - window.innerHeight * 0.3, behavior: reduce ? 'auto' : 'smooth' });
        });
      });
    });
    qa(root, '[data-ttmx-tabs]').forEach(initTabs);
    qa(root, '[data-ttmx-stack]').forEach(function (el) { if (!el.ttmxStack) new Stack(el); });
    qa(root, '[data-ttmx-mesh]').forEach(function (el) { if (!el.ttmxMesh) meshes.push(new Mesh(el)); });
    qa(root, '[data-ttmx-hscroll]').forEach(function (el) { if (!el.ttmxH) hscrolls.push(new HScroll(el)); });
    qa(root, '[data-ttmx-chapters]').forEach(function (el) { if (!el.ttmxCh) chapters.push(new Chapters(el)); });
    qa(root, '[data-ttmx-meshbg]').forEach(function (cv) { if (!cv.ttmxMesh) new MeshBg(cv); });
    qa(root, PAIR).forEach(initPair);
    if (farIO && !(window.Shopify && window.Shopify.designMode)) qa(root, '.ttmx-section').forEach(function (sec) { if (!sec.__far) { sec.__far = true; farIO.observe(sec); } });
    qa(root, '[data-ttmx-glow]').forEach(function (el) {
      if (el.__glow) return; el.__glow = true;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        el.style.setProperty('--mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
        el.style.setProperty('--my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
      });
    });
    qa(root, '.ttmx-section a[href^="#"], a[data-ttmx-jump]').forEach(function (a) {
      if (a.__jump) return; a.__jump = true;
      a.addEventListener('click', function (e) {
        var href = a.getAttribute('href') || '';
        if (href.charAt(0) !== '#' || href.length < 2) return;
        var t = null; try { t = document.querySelector(href); } catch (err) { t = null; }
        if (!t) return;
        e.preventDefault();
        var header = document.querySelector('.shopify-section-header, #shopify-section-header, header');
        var off = header ? Math.min(header.getBoundingClientRect().height, 120) : 0;
        window.scrollTo({ top: t.getBoundingClientRect().top + window.pageYOffset - off - 16, behavior: reduce ? 'auto' : 'smooth' });
      });
    });
    if (root === document || root.classList.contains('shopify-section') || root.classList.contains('ttmx-section')) { if (root !== document) glossary = null; initGlossary(root); }
    onScroll();
    kick();
  }

  // Scroll-driven sequences (What's inside, Modes, SOS Alerts): click or tap any step in the list or track to go
  // straight to it, before, during or after scrolling through. It scrolls the page to the middle of that step's
  // stretch of the pin, so the pin, the visual and the copy all stay in step with the scroll position.
  var STEP_SEL = '.ttmx-seq-track li[data-for-phase], .ttmx-hw-item[data-for-phase]';
  document.addEventListener('click', function (e) {
    var li = e.target.closest && e.target.closest(STEP_SEL); if (!li) return;
    var pin = li.closest('[data-ttmx-phases]'); if (!pin) return;
    var phases = parseInt(pin.getAttribute('data-ttmx-phases') || '0', 10), i = parseInt(li.getAttribute('data-for-phase'), 10);
    if (!phases || isNaN(i)) return;
    e.preventDefault();
    var vh = window.innerHeight, hdr = headerH(), r = pin.getBoundingClientRect();
    var span = Math.max(1, r.height - vh + hdr), p = Math.min(1, (i + 0.5) / phases);
    window.scrollTo({ top: r.top + window.pageYOffset - hdr + p * span, behavior: reduce ? 'auto' : 'smooth' });
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  window.TtmFx = { init: init, presets: Object.keys(PRESETS) };
})();
